/**
 * The dataProvider - the ONLY module that talks to Supabase for app data.
 * Screens use the typed hooks in `src/features/*`; those hooks call this.
 * Raising the data tier later (paid API, self-hosted aggregator) changes
 * Edge Functions + this file, nothing else.
 */
import { SEARCH } from '@/config';
import {
  averagePositionsSchema,
  matchShotSchema,
  parseOrNull,
  publicSquadSchema,
  type PublicSquad,
} from '@/lib/data/schemas';
import { requireSupabase } from '@/lib/supabase/client';
import type {
  BracketBoardEntry,
  BracketPayload,
  ChipType,
  FavoriteEntityType,
  Json,
  LeaderboardEntry,
  PredictionBoardEntry,
  PredictionPick,
  Tables,
  TransferFeedEntry,
} from '@/lib/supabase/types';

// ---------------------------------------------------------------------------
// Bzzoiro media helpers (pure URL builders — no DB round-trip).
// ---------------------------------------------------------------------------

/** Base for Bzzoiro's transparent cut-out player portraits (verified HTTP 200). */
const BZZOIRO_PLAYER_IMG = 'https://sports.bzzoiro.com/img/player';

/**
 * The Bzzoiro transparent CUT-OUT portrait for a player, keyed by their Bzzoiro
 * provider id (`match_player_stats.provider_player_id`, exposed on a rated
 * MatchPlayer). The `?sor=true` variant is the clean cut-out (nicer than a flat
 * headshot). Returns null when there's no id, so the caller can fall back to the
 * stored `photo_url` / initials. ONLY valid for Bzzoiro-sourced ids — never pass
 * an API-Football id here.
 */
export function bzzoiroCutout(providerPlayerId: string | number | null): string | null {
  if (providerPlayerId === null || providerPlayerId === undefined) return null;
  const id = String(providerPlayerId).trim();
  if (id === '') return null;
  return `${BZZOIRO_PLAYER_IMG}/${encodeURIComponent(id)}/?sor=true`;
}

// ---------------------------------------------------------------------------
// Composite row shapes for embedded selects (typed via .returns<>() until the
// author regenerates CLI types with full relationship metadata).
// ---------------------------------------------------------------------------

export interface TeamRef {
  id: string;
  provider_id: number;
  name: string;
  short_name: string | null;
  logo_url: string | null;
}

export interface CompetitionRef {
  id: string;
  name: string;
  logo_url: string | null;
  country_code: string | null;
  is_fantasy_enabled: boolean;
}

export interface FixtureWithRefs extends Tables<'fixtures'> {
  home_team: TeamRef | null;
  away_team: TeamRef | null;
  competition: CompetitionRef | null;
}

export interface MatchEventWithRefs extends Tables<'match_events'> {
  player: { id: string; name: string; known_as: string | null; photo_url: string | null } | null;
  assist: { id: string; name: string; known_as: string | null; photo_url: string | null } | null;
  team: TeamRef | null;
}

export interface PlayerMatchStatWithPlayer extends Tables<'player_match_stats'> {
  player: {
    id: string;
    name: string;
    known_as: string | null;
    position: string;
    photo_url: string | null;
    /** API-Football id - the join key to a lineup pitch player. */
    af_id: number | null;
  } | null;
}

export interface VenueRecord {
  played: number;
  won: number;
  drawn: number;
  lost: number;
  gf: number;
  ga: number;
  points: number;
}

export interface StandingWithTeam extends Tables<'standings'> {
  team: TeamRef | null;
  home?: VenueRecord | null;
  away?: VenueRecord | null;
}

export interface PlayerWithTeam extends Tables<'players'> {
  team: TeamRef | null;
}

export interface FantasySeasonWithCompetition extends Tables<'fantasy_seasons'> {
  competition: CompetitionRef | null;
}

export interface FantasyTeamWithSeason extends Tables<'fantasy_teams'> {
  fantasy_season: FantasySeasonWithCompetition | null;
}

export interface SquadPickWithPlayer extends Tables<'fantasy_squad_picks'> {
  player: PlayerWithTeam | null;
}

export interface MiniLeagueMembership {
  mini_league_id: string;
  mini_league: {
    id: string;
    name: string;
    invite_code: string;
    owner_id: string;
    fantasy_season_id: string;
    is_public: boolean;
  } | null;
}

const TEAM_REF = 'id, provider_id, name, short_name, logo_url';
const COMPETITION_REF = 'id, name, logo_url, country_code, is_fantasy_enabled';
const FIXTURE_SELECT = `*,
  home_team:teams!fixtures_home_team_id_fkey(${TEAM_REF}),
  away_team:teams!fixtures_away_team_id_fkey(${TEAM_REF}),
  competition:competitions!fixtures_competition_id_fkey(${COMPETITION_REF})`;

// Lean projection for LISTS: drops the heavy JSONB columns (lineups, team_stats,
// h2h, injuries, raw_payload) that list rows never render - ~83% smaller on the
// wire. Only the single-match getFixture keeps the full FIXTURE_SELECT.
const FIXTURE_LIST_SELECT = `id, provider_id, competition_id, season, round,
  home_team_id, away_team_id, kickoff_at, status, minute, home_score, away_score, venue, af_id,
  home_team:teams!fixtures_home_team_id_fkey(${TEAM_REF}),
  away_team:teams!fixtures_away_team_id_fkey(${TEAM_REF}),
  competition:competitions!fixtures_competition_id_fkey(${COMPETITION_REF})`;

function unwrap<T>(result: { data: T | null; error: { message: string } | null }): T {
  if (result.error) {
    throw new Error(result.error.message);
  }
  if (result.data === null) {
    throw new Error('NOT_FOUND');
  }
  return result.data;
}

// ---------------------------------------------------------------------------
// Match Center
// ---------------------------------------------------------------------------

export async function listCompetitions(): Promise<Tables<'competitions'>[]> {
  const db = requireSupabase();
  return unwrap(await db.from('competitions').select('*').order('name'));
}

export async function getCompetition(id: string): Promise<Tables<'competitions'>> {
  const db = requireSupabase();
  return unwrap(await db.from('competitions').select('*').eq('id', id).single());
}

/** Fixtures whose kickoff falls inside [fromIso, toIso) - one calendar day. */
export async function listFixturesForRange(
  fromIso: string,
  toIso: string,
): Promise<FixtureWithRefs[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fixtures')
      .select(FIXTURE_LIST_SELECT)
      .gte('kickoff_at', fromIso)
      .lt('kickoff_at', toIso)
      .order('kickoff_at')
      .returns<FixtureWithRefs[]>(),
  );
}

export async function listLiveFixtures(): Promise<FixtureWithRefs[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fixtures')
      .select(FIXTURE_LIST_SELECT)
      .eq('status', 'live')
      .order('kickoff_at')
      .returns<FixtureWithRefs[]>(),
  );
}

export async function listFixturesByCompetition(
  competitionId: string,
  scope: 'upcoming' | 'results',
  limit = 30,
): Promise<FixtureWithRefs[]> {
  const db = requireSupabase();
  const nowIso = new Date().toISOString();
  let query = db.from('fixtures').select(FIXTURE_LIST_SELECT).eq('competition_id', competitionId);
  if (scope === 'upcoming') {
    query = query.gte('kickoff_at', nowIso).order('kickoff_at', { ascending: true });
  } else {
    query = query.lt('kickoff_at', nowIso).order('kickoff_at', { ascending: false });
  }
  return unwrap(await query.limit(limit).returns<FixtureWithRefs[]>());
}

/** Every synced fixture of a competition season - input for the knockout bracket. */
export async function listCompetitionSeasonFixtures(
  competitionId: string,
  season: number,
): Promise<FixtureWithRefs[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fixtures')
      .select(FIXTURE_LIST_SELECT)
      .eq('competition_id', competitionId)
      .eq('season', season)
      .order('kickoff_at', { ascending: true })
      .limit(128) // bounds a full domestic season (WC is 64); the bracket never needs more
      .returns<FixtureWithRefs[]>(),
  );
}

export async function getFixture(id: string): Promise<FixtureWithRefs> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fixtures')
      .select(FIXTURE_SELECT)
      .eq('id', id)
      .single()
      .returns<FixtureWithRefs>(),
  );
}

export async function listMatchEvents(fixtureId: string): Promise<MatchEventWithRefs[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('match_events')
      .select(
        `*,
        player:players!match_events_player_id_fkey(id, name, known_as, photo_url),
        assist:players!match_events_assist_player_id_fkey(id, name, known_as, photo_url),
        team:teams!match_events_team_id_fkey(${TEAM_REF})`,
      )
      .eq('fixture_id', fixtureId)
      .order('minute')
      .returns<MatchEventWithRefs[]>(),
  );
}

const EVENT_SELECT = `*,
  player:players!match_events_player_id_fkey(id, name, known_as, photo_url),
  assist:players!match_events_assist_player_id_fkey(id, name, known_as, photo_url),
  team:teams!match_events_team_id_fkey(${TEAM_REF})`;

export interface MatchNewsItem {
  fixture: FixtureWithRefs;
  events: MatchEventWithRefs[];
}

/**
 * The News feed: recent finished fixtures plus their goal/card events, so the
 * client can template an auto bilingual recap. Two queries (fixtures, then a
 * batched event fetch) - no per-match round-trips.
 */
export async function getMatchNews(limit = 20): Promise<MatchNewsItem[]> {
  const db = requireSupabase();
  const fixtures = unwrap(
    await db
      .from('fixtures')
      .select(FIXTURE_LIST_SELECT)
      .eq('status', 'finished')
      .order('kickoff_at', { ascending: false })
      .limit(limit)
      .returns<FixtureWithRefs[]>(),
  );
  if (fixtures.length === 0) return [];
  const ids = fixtures.map((fixture) => fixture.id);
  const events = unwrap(
    await db
      .from('match_events')
      .select(EVENT_SELECT)
      .in('fixture_id', ids)
      .order('minute')
      .returns<MatchEventWithRefs[]>(),
  );
  const byFixture = new Map<string, MatchEventWithRefs[]>();
  for (const event of events) {
    const list = byFixture.get(event.fixture_id) ?? [];
    list.push(event);
    byFixture.set(event.fixture_id, list);
  }
  return fixtures.map((fixture) => ({ fixture, events: byFixture.get(fixture.id) ?? [] }));
}

export type NewsArticle = Tables<'news_articles'>;

/** Latest football news headlines for a language (RSS-sourced; the app links out). */
export async function getNewsHeadlines(lang: 'ar' | 'en', limit = 30): Promise<NewsArticle[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('news_articles')
      .select('*')
      .eq('lang', lang)
      .order('published_at', { ascending: false })
      .limit(limit)
      .returns<NewsArticle[]>(),
  );
}

export async function listPlayerMatchStats(
  fixtureId: string,
): Promise<PlayerMatchStatWithPlayer[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('player_match_stats')
      // Explicit columns (no raw_payload jsonb, ~36% of the row) - the ratings
      // UI only needs the structured stats.
      .select(
        `id, fixture_id, player_id, team_id, minutes, goals, assists, shots, passes,
         tackles, saves, goals_conceded, yellow, red, penalty_saved, penalty_missed,
         own_goals, rating,
         player:players!player_match_stats_player_id_fkey(id, name, known_as, position, photo_url, af_id)`,
      )
      .eq('fixture_id', fixtureId)
      .order('rating', { ascending: false, nullsFirst: false })
      .returns<PlayerMatchStatWithPlayer[]>(),
  );
}

// --- Advanced match stats: xG + shotmap (get_match_xg RPC) -------------------

/** Per-phase split for a stat (whole match / each half), both sides. */
export interface XgHalfSplit {
  all: { home: number; away: number };
  first_half: { home: number; away: number };
  second_half: { home: number; away: number };
}

/** Which phase a full-stats row reads from. */
export type XgPhase = 'all' | 'first_half' | 'second_half';

/** One full-stats group: a stat key → its per-phase home/away split. */
export type XgStatGroup = Record<string, XgHalfSplit>;

/**
 * The complete SofaScore-style grouped stat table (jsonb `stats_full`). Each
 * group is a map of `statName → {all, first_half, second_half}`; any group may
 * be absent for a fixture (skip it when rendering).
 */
export interface MatchStatsFull {
  overview?: XgStatGroup;
  shots?: XgStatGroup;
  attack?: XgStatGroup;
  passes?: XgStatGroup;
  duels?: XgStatGroup;
  defending?: XgStatGroup;
  goalkeeping?: XgStatGroup;
  np_expected_goals?: XgStatGroup;
}

/** Team-level advanced stats for a fixture (null until synced). */
export interface MatchXgStats {
  /**
   * The Bzzoiro live event id for this fixture (a numeric STRING), set pre-match
   * by the sync layer (so it is known before kickoff). It is the `event_id` the
   * live ball-tracking WebSocket subscribes to. null when the provider has no
   * live mapping for this fixture.
   */
  provider_match_id: string | null;
  home_xg: number;
  away_xg: number;
  home_possession: number;
  away_possession: number;
  home_big_chances: number;
  away_big_chances: number;
  home_shots: number;
  away_shots: number;
  home_shots_on_target: number;
  away_shots_on_target: number;
  /** Pre-match 1X2 decimal odds + the bookmaker, when synced. */
  odds_home: number | null;
  odds_draw: number | null;
  odds_away: number | null;
  odds_bookmaker: string | null;
  /** The full grouped stat table (SofaScore-style), null until synced. */
  stats_full: MatchStatsFull | null;
  /** Optional per-half breakdown of the headline metrics. */
  halves: {
    expected_goals: XgHalfSplit;
    ball_possession: XgHalfSplit;
    big_chances: XgHalfSplit;
    total_shots: XgHalfSplit;
    shots_on_target: XgHalfSplit;
  } | null;
  /**
   * Bzzoiro per-minute cumulative xG series. `cum_home`/`cum_away` are ALREADY
   * cumulative by minute, so they drive the momentum curve authoritatively (more
   * accurate than re-deriving it from shots). null on legacy rows / providers
   * that did not supply it — the UI falls back to the shot-derived curve.
   */
  xg_per_minute:
    | { m: number; xg_home: number; xg_away: number; cum_home: number; cum_away: number }[]
    | null;
  /** Bzzoiro signed momentum series (home-positive ~−100..100), null when absent. */
  momentum: { m: number; v: number }[] | null;
  /**
   * Bzzoiro average player positions. `x` is each team's own distance-from-own-
   * goal in its OWN attacking frame (NOT a shared 0–100 grid): the keeper sits at
   * the MINIMUM x (~7–14) and the deepest outfielder ~66–85, so x spans ~0–85 per
   * side. `y` IS a genuine 0–100 pitch-WIDTH value (verified ~16–89). The UI
   * therefore mirrors per side (home own-goal = left edge, away own-goal = right
   * edge) so the two formations face across the halfway line. null/empty when the
   * provider did not supply it.
   */
  average_positions: {
    home: AveragePositionPlayer[];
    away: AveragePositionPlayer[];
  } | null;
  /**
   * Pre-match model forecast (filled for UPCOMING fixtures): a 1X2 read, the
   * most-likely scoreline, and BTTS / over-under markets. null on matches the
   * model hasn't projected (or any finished fixture without a forecast).
   */
  prediction: MatchPrediction | null;
  /**
   * Pre-match predicted XI for each side (filled for UPCOMING fixtures): the
   * formation + the projected eleven grouped by position, with an AI confidence
   * score per player. null until the provider supplies a projection.
   */
  predicted_lineup: PredictedLineup | null;
  /**
   * Where to watch: the fixture's TV / streaming broadcasts (filled for
   * UPCOMING fixtures). null/empty when no broadcast listing was synced.
   */
  broadcasts: MatchBroadcast[] | null;
  /**
   * Matchday weather off the Bzzoiro event detail (migration 127):
   * {code, description ("cloudy"), wind_speed, temperature_c}. null when the
   * provider supplied no forecast for this fixture.
   */
  weather?: {
    code: number;
    description: string;
    wind_speed: number;
    temperature_c: number;
  } | null;
  /**
   * Venue / stadium card off /venues/{id}/ (migration 127): name + city +
   * country + capacity + geo + build year, plus `image_url` (the free, no-auth
   * venue photo). null when the event carried no venue_id (or the venue fetch
   * failed). The synced object additionally carries country_code / pitch
   * dimensions; only the headline fields are typed here.
   */
  venue?: {
    name: string;
    city: string;
    country: string;
    capacity: number;
    latitude: number;
    longitude: number;
    built_year: number;
    image_url: string;
  } | null;
  /**
   * Post-game highlight clips off the event detail (migration 127):
   * [{kind, title, url, thumbnail, published_at}]. Empty pre-match, filled with
   * real clips once the match has finished. null on legacy/unsynced rows.
   */
  highlights?: {
    kind: string;
    title: string;
    url: string;
    thumbnail: string;
    published_at: string;
  }[] | null;
  /**
   * Derived matchday context flags off the event detail (migration 127):
   * pitch condition, attendance, derby / neutral-ground markers, travel
   * distance. null on legacy/unsynced rows.
   */
  match_context?: {
    pitch_condition: number;
    attendance: number | null;
    is_local_derby: boolean;
    is_neutral_ground: boolean;
    travel_distance_km: number | null;
  } | null;
  /**
   * The 365scores game id this fixture maps to (migration 128), set by sync-365's
   * name+date join. The key the match trends / match news 365 endpoints subscribe
   * to. null when no 365 mapping exists for this fixture.
   */
  scores365_game_id?: number | null;
  /**
   * Per-match FORM trends from 365scores (migration 128). Presented as FORM
   * trends, NOT betting: each row keeps {id, lineTypeId, text, cause, outcome,
   * percentage, competitorIds, isGeneralGameBet, gameId} with the bet CTA dropped
   * at sync time. `text` reads e.g. "Over 2.5 Goals - 6/7 Last Matches";
   * `percentage` is 0..1. null/empty until synced.
   */
  trends?: MatchTrend[] | null;
}

/** One 365scores FORM trend row (betting CTA stripped). */
export interface MatchTrend {
  id: number;
  lineTypeId: number | null;
  /** Display text, e.g. "Over 2.5 Goals - 6/7 Last Matches". */
  text: string;
  /** Short cause, e.g. "Over 2.5 Goals" / "Colombia won". */
  cause: string | null;
  outcome: number | null;
  /** Hit rate 0..1 (0.857 = 6/7). */
  percentage: number | null;
  /** 365 competitor ids the trend concerns (one side, or both for general bets). */
  competitorIds: number[];
  /** true = applies to the whole game (Over/Under, BTTS); false = one team. */
  isGeneralGameBet: boolean;
  gameId: number | null;
}

/** A single 1X2 / score / market read from the pre-match model. */
export interface MatchPrediction {
  model: { version: string; confidence: number };
  markets: {
    match_result: {
      predicted: 'H' | 'D' | 'A';
      prob_home: number;
      prob_draw: number;
      prob_away: number;
    };
    score: { most_likely: string };
    btts: { prob_yes: number };
    over_under: {
      prob_over_15: number;
      prob_over_25: number;
      prob_over_35: number;
    };
  };
}

/** One projected player on a predicted lineup (position-grouped on the team column). */
export interface PredictedLineupPlayer {
  id: number | string;
  name: string;
  short_name: string | null;
  position: 'G' | 'D' | 'M' | 'F';
  jersey_number: number;
  ai_score: number;
}

/** One side's predicted XI: formation + the projected eleven + model confidence. */
export interface PredictedLineupSide {
  team_name?: string;
  formation?: string;
  confidence?: number;
  players: PredictedLineupPlayer[];
}

/** The pre-match predicted lineup for both sides. */
export interface PredictedLineup {
  home: PredictedLineupSide;
  away: PredictedLineupSide;
  lineup_status?: string;
  beta?: boolean;
}

/** A single TV / streaming broadcast entry for a fixture. */
export interface MatchBroadcast {
  channel_name: string;
  country_code: string;
  channel_link: string;
}

/** One player's average position on the formation map. `x` = distance from the
 *  team's OWN goal in its own attacking frame (~0–85; keeper lowest); `y` = pitch
 *  width (~0–100). The UI rescales + mirrors per side (see MatchXgStats). */
export interface AveragePositionPlayer {
  n: number;
  x: number;
  y: number;
  pos: string | null;
  name: string;
  player_id: number | null;
}

/** One shot on the shotmap. `x` = distance from the goal the shooter ATTACKED in
 *  that team's own frame (~0–85; ~0 on the goal line), so the UI plots each shot
 *  toward the goal its team attacks (home → right goal, away → left). `y` = pitch
 *  width (~0–100). `xg` 0–1 sizes the dot. */
export interface MatchShot {
  team_side: 'home' | 'away';
  player_name: string;
  minute: number;
  x: number;
  y: number;
  xg: number;
  result: string;
  situation: string;
  body_part: string;
  is_goal: boolean;
  is_on_target: boolean;
  is_headed: boolean;
  is_penalty: boolean;
  is_outside_box: boolean;
  is_blocked: boolean;
  /**
   * Goal-mouth coordinates: where the shot crossed (or would have crossed) the
   * goal line. `gm_y` is the horizontal position across the goal width (centre
   * ≈ 50, posts ≈ 44/56), `gm_z` the height (0 = ground, crossbar ≈ 40). All
   * nullable - only on-frame shots carry them. `block_*` mirror these for the
   * point a shot was blocked. `goal_mouth_location` is a label e.g. "low-centre".
   */
  gm_x: number | null;
  gm_y: number | null;
  gm_z: number | null;
  goal_mouth_location: string | null;
  block_x: number | null;
  block_y: number | null;
  block_z: number | null;
}

/**
 * One player's match line (sorted by rating desc by the RPC). `raw` carries the
 * provider's grouped stat blocks for the tap-to-expand detail strip.
 */
export interface MatchPlayer {
  player_name: string;
  team_side: 'home' | 'away';
  /** Bzzoiro provider player id (match_player_stats.provider_player_id), the key
   *  for the transparent cut-out portrait via `bzzoiroCutout`. Nullable: a player
   *  line can exist before the provider id is resolved. */
  provider_player_id: string | null;
  position: string | null;
  minutes: number;
  started: boolean;
  rating: number | null;
  goals: number;
  assists: number;
  total_shots: number;
  shots_on_target: number;
  xg: number;
  xa: number;
  key_passes: number;
  big_chances_created: number;
  total_passes: number;
  accurate_passes: number;
  /** Provider's grouped blocks (passing / shooting / duels / defending / …). */
  raw: Json | null;
  /** TheStatsAPI player headshot URL (from get_match_xg), or null when absent. */
  photo_url: string | null;
}

/** Pre-match 1X2 decimal odds + the bookmaker, or null when unsynced. */
export interface MatchOdds {
  home: number;
  draw: number;
  away: number;
  bookmaker: string | null;
}

/** The match referee + their season-long card record, or null when unsynced. */
export interface MatchReferee {
  name: string;
  country: string | null;
  country_code: string | null;
  career: {
    games: number;
    yellow_cards: number;
    red_cards: number;
    yellow_red_cards: number;
  } | null;
}

export interface MatchXg {
  stats: MatchXgStats | null;
  shots: MatchShot[];
  players: MatchPlayer[];
  odds: MatchOdds | null;
  referee: MatchReferee | null;
}

/**
 * xG + shotmap + player ratings + odds for a fixture, via the get_match_xg RPC.
 * Returns an empty shape (everything null/empty) when the match has no advanced
 * data yet - the UI silent-hides on that, so a missing sync never shows a broken
 * card. Each slice (stats / shots / players / odds) populates independently, so
 * every section degrades on its own.
 */
export async function getMatchXg(fixtureId: string): Promise<MatchXg> {
  const db = requireSupabase();
  // Cast the RPC name/args/result: the generated types lag new functions until
  // `supabase gen types` reruns; the call is valid at runtime.
  const { data, error } = await db.rpc('get_match_xg' as never, {
    p_fixture_id: fixtureId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  const payload = (data ?? null) as {
    stats?: MatchXgStats | null;
    shots?: MatchShot[];
    players?: MatchPlayer[];
    odds?: MatchOdds | null;
    referee?: MatchReferee | null;
  } | null;

  const rawStats = payload?.stats ?? null;
  // The two coordinate-plotted slices (average positions + shotmap dots) are the
  // only payload fields rendered DIRECTLY off provider x/y numbers, so a renamed
  // or NaN field would otherwise paint a dot at the card corner. Parse them
  // defensively (mirroring lineups / team_stats): a malformed positions blob
  // self-hides (→ null), and individual malformed shot rows drop instead of
  // poisoning the whole map. Everything else passes through unchanged.
  const stats: MatchXgStats | null = rawStats
    ? {
        ...rawStats,
        average_positions: rawStats.average_positions
          ? parseOrNull(averagePositionsSchema, rawStats.average_positions)
          : null,
      }
    : null;

  const shots: MatchShot[] = (payload?.shots ?? []).flatMap((shot) => {
    const parsed = parseOrNull(matchShotSchema, shot);
    return parsed ? [parsed as MatchShot] : [];
  });

  return {
    stats,
    shots,
    players: payload?.players ?? [],
    odds: payload?.odds ?? null,
    referee: payload?.referee ?? null,
  };
}

// --- Live in-match stats (get_live_match_stats RPC) --------------------------

/** A home/away pair for one live stat key. */
export interface LiveStatPair {
  home: number;
  away: number;
}

/**
 * The live snapshot for an in-progress fixture (get_live_match_stats RPC).
 * Polled every 30s only while the match is live. `stats` is keyed by stat name;
 * read live xG from `stats.expected_goals` (the top-level home_xg/away_xg can be
 * null early). null when there's no fresh row yet (the UI silent-hides on that).
 */
export interface LiveMatchStats {
  status: string;
  /**
   * The Bzzoiro live event id for this fixture (numeric STRING). Flows through
   * `to_jsonb` from the same row as the xG stats, so the live ball-tracker can
   * subscribe even when only the live snapshot is loaded. null when unmapped.
   */
  provider_match_id?: string | null;
  elapsed: number | null;
  period: string | null;
  home_goals: number | null;
  away_goals: number | null;
  home_xg: number | null;
  away_xg: number | null;
  stats: {
    ball_possession?: LiveStatPair;
    total_shots?: LiveStatPair;
    shots_on_target?: LiveStatPair;
    corner_kicks?: LiveStatPair;
    fouls?: LiveStatPair;
    offsides?: LiveStatPair;
    big_chances?: LiveStatPair;
    yellow_cards?: LiveStatPair;
    expected_goals?: LiveStatPair;
    goalkeeper_saves?: LiveStatPair;
    [key: string]: LiveStatPair | undefined;
  };
  /**
   * Running signed momentum series (home-positive ~−100..100), refreshed by
   * sync-bzzoiro-live every ~3 min while the match is in play. Same shape as
   * MatchXgStats.momentum, so it drives the MomentumChart wave live; null/absent
   * on a live row that predates migration-133 or before the provider emits it.
   */
  momentum?: { m: number; v: number }[] | null;
  /**
   * Running cumulative-by-minute xG race, refreshed by sync-bzzoiro-live every
   * ~3 min while live. Same shape as MatchXgStats.xg_per_minute, so it drives the
   * XgMomentum curves live; null/absent on a pre-migration-133 row or early minutes.
   */
  xg_per_minute?:
    | { m: number; xg_home: number; xg_away: number; cum_home: number; cum_away: number }[]
    | null;
  updated_at: string;
}

/**
 * Live in-match stats for a fixture (a jsonb row or null). Returns null when the
 * match has no fresh live row (not started / already finished / not synced), so
 * the live ticker hides itself.
 */
export async function getLiveMatchStats(fixtureId: string): Promise<LiveMatchStats | null> {
  const db = requireSupabase();
  // Cast the RPC name/args/result: the generated types lag new functions until
  // `supabase gen types` reruns; the call is valid at runtime.
  const { data, error } = await db.rpc('get_live_match_stats' as never, {
    p_fixture_id: fixtureId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data as LiveMatchStats | null) ?? null;
}

export interface PlayerCard {
  af_id: number;
  name: string;
  known_as: string | null;
  position: string;
  photo_url: string | null;
  /** The player's CLUB crest (Liverpool, Napoli...) for the lineup overlay. */
  club_logo_url: string | null;
}

/** Photo/name cards keyed by API-Football id - to enrich a lineup pitch with
 *  player photos + club crests even before the match has stats. */
export async function listPlayerCardsByAfIds(afIds: number[]): Promise<PlayerCard[]> {
  if (afIds.length === 0) return [];
  const db = requireSupabase();
  return unwrap(
    await db
      .from('players')
      .select('af_id, name, known_as, position, photo_url, club_logo_url')
      .in('af_id', afIds)
      .returns<PlayerCard[]>(),
  );
}

/** All fixtures of one round (gameweek) in a competition season. */
export async function listFixturesByRound(
  competitionId: string,
  season: number,
  round: string,
): Promise<FixtureWithRefs[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fixtures')
      .select(FIXTURE_LIST_SELECT)
      .eq('competition_id', competitionId)
      .eq('season', season)
      .eq('round', round)
      .order('kickoff_at')
      .returns<FixtureWithRefs[]>(),
  );
}

/** Past meetings between two teams (any competition), most recent first. Derived
 *  from already-stored finished fixtures - no extra API call. */
export async function listHeadToHead(
  teamA: string,
  teamB: string,
  before: string,
): Promise<FixtureWithRefs[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fixtures')
      .select(FIXTURE_LIST_SELECT)
      .or(
        `and(home_team_id.eq.${teamA},away_team_id.eq.${teamB}),and(home_team_id.eq.${teamB},away_team_id.eq.${teamA})`,
      )
      .eq('status', 'finished')
      .lt('kickoff_at', before)
      .order('kickoff_at', { ascending: false })
      .limit(10)
      .returns<FixtureWithRefs[]>(),
  );
}

/** Events across several fixtures (provisional live fantasy points). */
export async function listEventsForFixtures(
  fixtureIds: string[],
): Promise<Tables<'match_events'>[]> {
  if (fixtureIds.length === 0) return [];
  const db = requireSupabase();
  return unwrap(
    await db.from('match_events').select('*').in('fixture_id', fixtureIds).order('minute'),
  );
}

export async function getStandings(
  competitionId: string,
  season: number,
): Promise<StandingWithTeam[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('standings')
      .select(`*, team:teams!standings_team_id_fkey(${TEAM_REF})`)
      .eq('competition_id', competitionId)
      .eq('season', season)
      .order('group_label', { ascending: true, nullsFirst: true })
      .order('rank')
      .returns<StandingWithTeam[]>(),
  );
}

/** All synced standings rows for several competitions (prediction model input). */
export async function listStandingsForCompetitions(
  competitionIds: string[],
): Promise<Tables<'standings'>[]> {
  if (competitionIds.length === 0) return [];
  const db = requireSupabase();
  return unwrap(
    await db.from('standings').select('*').in('competition_id', competitionIds),
  );
}

export async function listTeamsByCompetition(competitionId: string): Promise<Tables<'teams'>[]> {
  const db = requireSupabase();
  // Teams that appear in this competition's standings (covers cups + leagues),
  // falling back to primary-competition teams when standings aren't synced yet.
  const standings = await db
    .from('standings')
    .select(`team:teams!standings_team_id_fkey(*)`)
    .eq('competition_id', competitionId)
    .returns<{ team: Tables<'teams'> | null }[]>();
  if (!standings.error && standings.data.length > 0) {
    const teams = standings.data
      .map((row) => row.team)
      .filter((team): team is Tables<'teams'> => team !== null);
    const unique = new Map(teams.map((team) => [team.id, team]));
    return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
  }
  return unwrap(
    await db.from('teams').select('*').eq('competition_id', competitionId).order('name'),
  );
}

export async function getTeam(id: string): Promise<Tables<'teams'>> {
  const db = requireSupabase();
  return unwrap(await db.from('teams').select('*').eq('id', id).single());
}

/** A broad team list for discovery / onboarding (popularity is sorted client-side). */
export async function listAllTeams(limit = 200): Promise<Tables<'teams'>[]> {
  const db = requireSupabase();
  return unwrap(await db.from('teams').select('*').order('name').limit(limit));
}

/** Notable players to follow during onboarding: those with a photo, the priciest
 *  (a proxy for fame) first, with their team for the crest/flag. */
export async function listFollowablePlayers(limit = 40): Promise<PlayerWithTeam[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('players')
      .select(`*, team:teams!players_team_id_fkey(${TEAM_REF})`)
      .not('photo_url', 'is', null)
      .order('fantasy_price', { ascending: false })
      .order('name')
      .limit(limit)
      .returns<PlayerWithTeam[]>(),
  );
}

/** Resolve a set of followed player ids to full player rows (with team). */
export async function listPlayersByIds(ids: string[]): Promise<PlayerWithTeam[]> {
  if (ids.length === 0) return [];
  const db = requireSupabase();
  return unwrap(
    await db
      .from('players')
      .select(`*, team:teams!players_team_id_fkey(${TEAM_REF})`)
      .in('id', ids)
      .returns<PlayerWithTeam[]>(),
  );
}

export async function listTeamPlayers(teamId: string): Promise<Tables<'players'>[]> {
  const db = requireSupabase();
  return unwrap(
    await db.from('players').select('*').eq('team_id', teamId).order('position').order('name'),
  );
}

export async function getPlayer(id: string): Promise<PlayerWithTeam> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('players')
      .select(`*, team:teams!players_team_id_fkey(${TEAM_REF})`)
      .eq('id', id)
      .single()
      .returns<PlayerWithTeam>(),
  );
}

export interface PlayerMatchRecent extends Tables<'player_match_stats'> {
  fixture: {
    id: string;
    kickoff_at: string;
    status: string;
    home_team_id: string | null;
    away_team_id: string | null;
    home_score: number | null;
    away_score: number | null;
    home_team: { name: string; short_name: string | null; logo_url: string | null } | null;
    away_team: { name: string; short_name: string | null; logo_url: string | null } | null;
  } | null;
}

export interface PlayerSeasonAggregates {
  appearances: number;
  minutes: number;
  goals: number;
  assists: number;
  yellow: number;
  red: number;
  cleanSheets: number;
  averageRating: number | null;
  recent: PlayerMatchRecent[];
}

const RECENT_FIXTURE = `fixture:fixtures!player_match_stats_fixture_id_fkey(
  id, kickoff_at, status, home_team_id, away_team_id, home_score, away_score,
  home_team:home_team_id(name, short_name, logo_url),
  away_team:away_team_id(name, short_name, logo_url)
)`;

export async function getPlayerSeasonStats(playerId: string): Promise<PlayerSeasonAggregates> {
  const db = requireSupabase();
  const rows = unwrap(
    await db
      .from('player_match_stats')
      .select(`*, ${RECENT_FIXTURE}`)
      .eq('player_id', playerId)
      .order('created_at', { ascending: false })
      .limit(60)
      .returns<PlayerMatchRecent[]>(),
  );
  const played = rows.filter((row) => row.minutes > 0);
  const ratings = played
    .map((row) => row.rating)
    .filter((rating): rating is number => rating !== null);
  return {
    appearances: played.length,
    minutes: played.reduce((sum, row) => sum + row.minutes, 0),
    goals: played.reduce((sum, row) => sum + row.goals, 0),
    assists: played.reduce((sum, row) => sum + row.assists, 0),
    yellow: played.reduce((sum, row) => sum + row.yellow, 0),
    red: played.reduce((sum, row) => sum + row.red, 0),
    cleanSheets: played.filter((row) => row.minutes >= 60 && row.goals_conceded === 0).length,
    averageRating:
      ratings.length > 0
        ? Math.round((ratings.reduce((sum, r) => sum + Number(r), 0) / ratings.length) * 100) / 100
        : null,
    recent: rows.slice(0, 5),
  };
}

export async function getTopPlayers(
  competitionId: string,
  season: number,
  limit = 20,
  statType: 'goals' | 'assists' = 'goals',
): Promise<import('@/lib/supabase/types').TopPlayerEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_top_players', {
    p_competition_id: competitionId,
    p_season: season,
    p_limit: limit,
    p_stat_type: statType,
  });
  if (error) throw new Error(error.message);
  return data ?? [];
}

export interface SearchResults {
  players: PlayerWithTeam[];
  teams: Tables<'teams'>[];
  competitions: Tables<'competitions'>[];
}

export async function searchAll(query: string): Promise<SearchResults> {
  const db = requireSupabase();
  const term = `%${query.replace(/[%_]/g, '')}%`;
  const [players, teams, competitions] = await Promise.all([
    db
      .from('players')
      .select(`*, team:teams!players_team_id_fkey(${TEAM_REF})`)
      .or(`name.ilike.${term},known_as.ilike.${term}`)
      .limit(SEARCH.maxResults)
      .returns<PlayerWithTeam[]>(),
    db.from('teams').select('*').ilike('name', term).limit(SEARCH.maxResults),
    db.from('competitions').select('*').ilike('name', term).limit(SEARCH.maxResults),
  ]);
  return {
    players: unwrap(players),
    teams: unwrap(teams),
    competitions: unwrap(competitions),
  };
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export async function getProfile(userId: string): Promise<Tables<'profiles'> | null> {
  const db = requireSupabase();
  const result = await db.from('profiles').select('*').eq('id', userId).maybeSingle();
  if (result.error) throw new Error(result.error.message);
  return result.data;
}

export async function updateProfile(
  userId: string,
  patch: Partial<
    Pick<
      Tables<'profiles'>,
      | 'display_name'
      | 'locale'
      | 'prefers_arabic_numerals'
      | 'favorite_team_id'
      | 'avatar_url'
      | 'display_name_confirmed'
    >
  >,
): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.from('profiles').update(patch).eq('id', userId);
  if (error) {
    // 23505 = unique_violation on the case-insensitive display_name index.
    if (error.code === '23505') throw new Error('USERNAME_TAKEN');
    throw new Error(error.message);
  }
}

/** Whether a username (display_name) is free, case-insensitively. */
export async function isDisplayNameAvailable(name: string): Promise<boolean> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('is_display_name_available', { p_name: name });
  if (error) throw new Error(error.message);
  return Boolean(data);
}

// ---------------------------------------------------------------------------
// Fantasy Challenges (the per-round bonus mini-game).
// ---------------------------------------------------------------------------
export interface ChallengeOption {
  id: string;
  label: string;
}

export interface ChallengeTeamRef {
  name: string;
  logo: string | null;
}

export interface ChallengeWithState {
  id: string;
  prompt: string;
  options: ChallengeOption[];
  points: number;
  status: 'open' | 'locked' | 'resolved';
  correctOption: string | null;
  lockAt: string;
  myOption: string | null;
  myPoints: number | null;
  tally: Record<string, number>;
  totalVotes: number;
  // For the daily per-match challenges: the resolver kind + the fixture's two
  // teams, so the client can render a localized prompt + localized team names.
  resolverKind: string | null;
  home: ChallengeTeamRef | null;
  away: ChallengeTeamRef | null;
}

/** Open + recent challenges with the caller's pick and the community tally. */
export async function getChallenges(): Promise<ChallengeWithState[]> {
  const db = requireSupabase();
  // Cast through unknown: the generated types lag the fixture_id column + the
  // fixtures->teams embed until `supabase gen types` reruns; both are valid at
  // runtime (the embed mirrors FIXTURE_SELECT).
  const challenges = unwrap(
    await db
      .from('fantasy_challenges')
      .select('id, prompt, options, points, status, correct_option, lock_at, fixture_id, resolver_kind')
      // Only show answerable questions: open + not yet locked, soonest first. This
      // keeps the list to fresh upcoming-match challenges (the stale, already
      // locked rows that used to fill the screen are excluded).
      .eq('status', 'open')
      .gt('lock_at', new Date().toISOString())
      .order('lock_at', { ascending: true })
      .limit(30),
  ) as unknown as {
    id: string;
    prompt: string;
    options: ChallengeOption[];
    points: number;
    status: ChallengeWithState['status'];
    correct_option: string | null;
    lock_at: string;
    fixture_id: string | null;
    resolver_kind: string | null;
  }[];
  if (challenges.length === 0) return [];
  const ids = challenges.map((c) => c.id as string);
  // RLS returns only the caller's own picks.
  const picks = unwrap(
    await db.from('fantasy_challenge_picks').select('challenge_id, option_id, points').in('challenge_id', ids),
  );
  const pickByCh = new Map(picks.map((p) => [p.challenge_id as string, p]));

  // The two teams for any per-match challenge, so the client can localize the
  // prompt + team names (the stored prompt is just an English fallback).
  const fixtureIds = [...new Set(challenges.map((c) => c.fixture_id as string | null).filter(Boolean))] as string[];
  const fixtureTeams = new Map<string, { home: ChallengeTeamRef | null; away: ChallengeTeamRef | null }>();
  if (fixtureIds.length > 0) {
    const fixtures = unwrap(
      await db
        .from('fixtures')
        .select(
          'id, home_team:teams!fixtures_home_team_id_fkey(name, logo_url), away_team:teams!fixtures_away_team_id_fkey(name, logo_url)',
        )
        .in('id', fixtureIds),
    ) as unknown as {
      id: string;
      home_team: { name: string; logo_url: string | null } | null;
      away_team: { name: string; logo_url: string | null } | null;
    }[];
    for (const f of fixtures) {
      const home = f.home_team;
      const away = f.away_team;
      fixtureTeams.set(f.id, {
        home: home ? { name: home.name, logo: home.logo_url } : null,
        away: away ? { name: away.name, logo: away.logo_url } : null,
      });
    }
  }
  // One batched tally call for ALL challenges (was N parallel RPCs every 30s).
  // The function applies the per-challenge "no peeking" guard server-side.
  const { data: tallyRows } = await db.rpc('get_challenge_tallies' as never, { p_ids: ids } as never);
  const tallyByCh = new Map<string, Record<string, number>>();
  const totalByCh = new Map<string, number>();
  for (const row of (tallyRows ?? []) as unknown as {
    challenge_id: string;
    option_id: string;
    votes: number;
  }[]) {
    const entry = tallyByCh.get(row.challenge_id) ?? {};
    entry[row.option_id] = row.votes;
    tallyByCh.set(row.challenge_id, entry);
    totalByCh.set(row.challenge_id, (totalByCh.get(row.challenge_id) ?? 0) + row.votes);
  }
  return challenges.map((c) => {
    const tally = tallyByCh.get(c.id as string) ?? {};
    const total = totalByCh.get(c.id as string) ?? 0;
    const mine = pickByCh.get(c.id as string);
    const teams = c.fixture_id ? fixtureTeams.get(c.fixture_id as string) : undefined;
    return {
      id: c.id as string,
      prompt: c.prompt as string,
      options: (c.options ?? []) as unknown as ChallengeOption[],
      points: c.points as number,
      status: c.status as ChallengeWithState['status'],
      correctOption: (c.correct_option as string | null) ?? null,
      lockAt: c.lock_at as string,
      myOption: (mine?.option_id as string | undefined) ?? null,
      myPoints: (mine?.points as number | null | undefined) ?? null,
      tally,
      totalVotes: total,
      resolverKind: (c.resolver_kind as string | null) ?? null,
      home: teams?.home ?? null,
      away: teams?.away ?? null,
    };
  });
}

export async function submitChallengePick(challengeId: string, optionId: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('submit_challenge_pick', {
    p_challenge_id: challengeId,
    p_option_id: optionId,
  });
  if (error) throw new Error(error.message);
}

export async function getMyChallengePoints(): Promise<number> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('my_challenge_points');
  if (error) throw new Error(error.message);
  return data ?? 0;
}

export interface TransferState {
  has_team: boolean;
  has_history?: boolean;
  free_transfers?: number;
  transfers_made?: number;
  projected_hit?: number;
  upcoming_gameweek_id?: string | null;
  active_chip?: 'triple_captain' | 'bench_boost' | 'free_hit' | 'wildcard' | null;
}

/** The caller's live transfer picture for a season (changes vs the last locked
 *  squad, the projected −4 hit, free transfers, any chip armed). */
export async function getTransferState(fantasySeasonId: string): Promise<TransferState> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_transfer_state', {
    p_fantasy_season_id: fantasySeasonId,
  });
  if (error) throw new Error(error.message);
  return (data ?? { has_team: false }) as unknown as TransferState;
}

export async function deleteMyAccount(): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('delete_my_account');
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Favorites (multi-follow: teams, competitions, players)
// ---------------------------------------------------------------------------

export async function listMyFavorites(): Promise<Tables<'user_favorites'>[]> {
  const db = requireSupabase();
  return unwrap(
    await db.from('user_favorites').select('*').order('created_at', { ascending: false }),
  );
}

export async function addFavorite(
  userId: string,
  entityType: FavoriteEntityType,
  entityId: string,
): Promise<void> {
  const db = requireSupabase();
  const { error } = await db
    .from('user_favorites')
    .upsert(
      { user_id: userId, entity_type: entityType, entity_id: entityId },
      { onConflict: 'user_id,entity_type,entity_id', ignoreDuplicates: true },
    );
  if (error) throw new Error(error.message);
}

export async function removeFavorite(
  entityType: FavoriteEntityType,
  entityId: string,
): Promise<void> {
  const db = requireSupabase();
  const { error } = await db
    .from('user_favorites')
    .delete()
    .eq('entity_type', entityType)
    .eq('entity_id', entityId);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Fantasy
// ---------------------------------------------------------------------------

export async function listFantasySeasons(): Promise<FantasySeasonWithCompetition[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fantasy_seasons')
      .select(`*, competition:competitions!fantasy_seasons_competition_id_fkey(${COMPETITION_REF})`)
      .eq('is_active', true)
      .order('created_at')
      .returns<FantasySeasonWithCompetition[]>(),
  );
}

export async function getFantasySeason(id: string): Promise<FantasySeasonWithCompetition> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fantasy_seasons')
      .select(`*, competition:competitions!fantasy_seasons_competition_id_fkey(${COMPETITION_REF})`)
      .eq('id', id)
      .single()
      .returns<FantasySeasonWithCompetition>(),
  );
}

export async function listGameweeks(fantasySeasonId: string): Promise<Tables<'gameweeks'>[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('gameweeks')
      .select('*')
      .eq('fantasy_season_id', fantasySeasonId)
      .order('number')
      .returns<Tables<'gameweeks'>[]>(),
  );
}

export async function listMyFantasyTeams(userId: string): Promise<FantasyTeamWithSeason[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fantasy_teams')
      .select(
        `*, fantasy_season:fantasy_seasons!fantasy_teams_fantasy_season_id_fkey(
          *, competition:competitions!fantasy_seasons_competition_id_fkey(${COMPETITION_REF}))`,
      )
      .eq('user_id', userId)
      .returns<FantasyTeamWithSeason[]>(),
  );
}

export async function getMyFantasyTeam(
  fantasySeasonId: string,
  userId: string,
): Promise<Tables<'fantasy_teams'> | null> {
  const db = requireSupabase();
  const result = await db
    .from('fantasy_teams')
    .select('*')
    .eq('fantasy_season_id', fantasySeasonId)
    .eq('user_id', userId)
    .maybeSingle();
  if (result.error) throw new Error(result.error.message);
  return result.data;
}

export async function listSquadPicks(fantasyTeamId: string): Promise<SquadPickWithPlayer[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fantasy_squad_picks')
      .select(
        `*, player:players!fantasy_squad_picks_player_id_fkey(
          *, team:teams!players_team_id_fkey(${TEAM_REF}))`,
      )
      .eq('fantasy_team_id', fantasyTeamId)
      .order('slot')
      .returns<SquadPickWithPlayer[]>(),
  );
}

/** The selectable player pool for a competition (teams synced via standings). */
export async function listFantasyPlayerPool(competitionId: string): Promise<PlayerWithTeam[]> {
  const db = requireSupabase();
  const teams = await listTeamsByCompetition(competitionId);
  if (teams.length === 0) return [];
  const teamIds = teams.map((team) => team.id);
  return unwrap(
    await db
      .from('players')
      .select(`*, team:teams!players_team_id_fkey(${TEAM_REF})`)
      .in('team_id', teamIds)
      .order('fantasy_price', { ascending: false })
      .order('name')
      .limit(2000)
      .returns<PlayerWithTeam[]>(),
  );
}

export async function getPlayerGwScores(
  gameweekId: string,
): Promise<Tables<'player_gameweek_scores'>[]> {
  const db = requireSupabase();
  return unwrap(
    await db.from('player_gameweek_scores').select('*').eq('gameweek_id', gameweekId),
  );
}

export async function getTeamGwScore(
  fantasyTeamId: string,
  gameweekId: string,
): Promise<Tables<'fantasy_team_gw_scores'> | null> {
  const db = requireSupabase();
  const result = await db
    .from('fantasy_team_gw_scores')
    .select('*')
    .eq('fantasy_team_id', fantasyTeamId)
    .eq('gameweek_id', gameweekId)
    .maybeSingle();
  if (result.error) throw new Error(result.error.message);
  return result.data;
}

export async function getLineupHistory(
  fantasyTeamId: string,
  gameweekId: string,
): Promise<Tables<'fantasy_lineup_history'> | null> {
  const db = requireSupabase();
  const result = await db
    .from('fantasy_lineup_history')
    .select('*')
    .eq('fantasy_team_id', fantasyTeamId)
    .eq('gameweek_id', gameweekId)
    .maybeSingle();
  if (result.error) throw new Error(result.error.message);
  return result.data;
}

export interface SquadPickInput {
  player_id: string;
  slot: number;
  is_starting: boolean;
  is_captain: boolean;
  is_vice: boolean;
}

export async function saveSquad(
  fantasySeasonId: string,
  teamName: string,
  picks: SquadPickInput[],
): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('save_squad', {
    p_fantasy_season_id: fantasySeasonId,
    p_team_name: teamName,
    p_picks: picks as unknown as Json,
  });
  if (error) throw new Error(error.message);
}

/** Every chip the team has armed/used (drives the chips strip's used/active state). */
export async function listTeamChips(fantasyTeamId: string): Promise<Tables<'fantasy_chips'>[]> {
  const db = requireSupabase();
  return unwrap(
    await db
      .from('fantasy_chips')
      .select('*')
      .eq('fantasy_team_id', fantasyTeamId)
      .returns<Tables<'fantasy_chips'>[]>(),
  );
}

export async function playChip(gameweekId: string, chipType: ChipType): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('play_chip', {
    p_gameweek_id: gameweekId,
    p_chip_type: chipType,
  });
  if (error) throw new Error(error.message);
}

export async function cancelChip(gameweekId: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('cancel_chip', { p_gameweek_id: gameweekId });
  if (error) throw new Error(error.message);
}

export async function getOverallLeaderboard(
  fantasySeasonId: string,
  limit = 50,
  offset = 0,
): Promise<LeaderboardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_overall_leaderboard', {
    p_fantasy_season_id: fantasySeasonId,
    p_limit: limit,
    p_offset: offset,
  });
  if (error) throw new Error(error.message);
  return data ?? [];
}

export interface GameweekBoardEntry {
  rank: number;
  fantasy_team_id: string;
  team_name: string;
  display_name: string;
  points: number;
  avatar_url: string | null;
}

/** Per-round fantasy ranking for a single gameweek. */
export async function getGameweekLeaderboard(
  gameweekId: string,
  limit = 50,
  offset = 0,
): Promise<GameweekBoardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_gameweek_leaderboard' as never, {
    p_gameweek_id: gameweekId,
    p_limit: limit,
    p_offset: offset,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data ?? []) as unknown as GameweekBoardEntry[];
}

export async function getMyRank(
  fantasySeasonId: string,
): Promise<{ rank: number; total_teams: number; total_points: number } | null> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_my_rank', { p_fantasy_season_id: fantasySeasonId });
  if (error) throw new Error(error.message);
  return data && data.length > 0 ? data[0] : null;
}

export async function listMyMiniLeagues(userId: string): Promise<MiniLeagueMembership[]> {
  const db = requireSupabase();
  const myTeams = await db.from('fantasy_teams').select('id').eq('user_id', userId);
  if (myTeams.error) throw new Error(myTeams.error.message);
  const teamIds = (myTeams.data ?? []).map((team) => team.id);
  if (teamIds.length === 0) return [];
  return unwrap(
    await db
      .from('mini_league_members')
      .select(
        `mini_league_id, mini_league:mini_leagues!mini_league_members_mini_league_id_fkey(
          id, name, invite_code, owner_id, fantasy_season_id, is_public)`,
      )
      .in('fantasy_team_id', teamIds)
      .returns<MiniLeagueMembership[]>(),
  );
}

export async function getMiniLeagueLeaderboard(leagueId: string): Promise<LeaderboardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_mini_league_leaderboard', { p_league_id: leagueId });
  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * A rival manager's revealed squad for a (resolved) gameweek + per-player
 * points. The server only exposes lineups whose deadline has passed (the caller
 * always sees their own). The jsonb is parsed at the trust boundary.
 */
export async function getPublicSquad(
  fantasyTeamId: string,
  gameweekId?: string,
): Promise<PublicSquad> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_public_squad', {
    p_fantasy_team_id: fantasyTeamId,
    ...(gameweekId ? { p_gameweek_id: gameweekId } : {}),
  });
  if (error) throw new Error(error.message);
  const parsed = publicSquadSchema.safeParse(data);
  if (!parsed.success) throw new Error('unknown');
  return parsed.data;
}

export async function createMiniLeague(
  fantasySeasonId: string,
  name: string,
): Promise<{ id: string; name: string; invite_code: string }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('create_mini_league', {
    p_fantasy_season_id: fantasySeasonId,
    p_name: name,
  });
  if (error) throw new Error(error.message);
  const parsed = data as { id?: string; name?: string; invite_code?: string } | null;
  if (!parsed?.id || !parsed.name || !parsed.invite_code) {
    throw new Error('unknown');
  }
  return { id: parsed.id, name: parsed.name, invite_code: parsed.invite_code };
}

export async function joinMiniLeague(code: string): Promise<{ id: string; name: string }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('join_mini_league', { p_code: code });
  if (error) throw new Error(error.message);
  const parsed = data as { id?: string; name?: string } | null;
  if (!parsed?.id || !parsed.name) {
    throw new Error('unknown');
  }
  return { id: parsed.id, name: parsed.name };
}

export async function leaveMiniLeague(leagueId: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('leave_mini_league', { p_league_id: leagueId });
  if (error) throw new Error(error.message);
}

// --- Friend Leagues: private cross-game groups (+ 1v1 duels) -----------------
// Members are ranked by ONE unified season score that sums points across every
// game, so friends compete on everything at once.

export interface FriendLeague {
  id: string;
  name: string;
  invite_code: string;
  is_duel: boolean;
  owner_id: string;
  member_count: number;
  my_score: number;
}

export interface FriendLeagueBoardEntry {
  rank: number;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  score: number;
  is_me: boolean;
}

export async function getMyFriendLeagues(): Promise<FriendLeague[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_my_friend_leagues' as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data as FriendLeague[] | null) ?? [];
}

export async function getFriendLeagueBoard(leagueId: string): Promise<FriendLeagueBoardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_friend_league_board' as never, {
    p_league_id: leagueId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data as FriendLeagueBoardEntry[] | null) ?? [];
}

export async function createFriendLeague(
  name: string,
  isDuel: boolean,
  seasonId?: string,
): Promise<{ id: string; name: string; invite_code: string; is_duel: boolean }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('create_friend_league' as never, {
    p_name: name,
    p_is_duel: isDuel,
    ...(seasonId ? { p_season_id: seasonId } : {}),
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  const parsed = data as { id?: string; name?: string; invite_code?: string; is_duel?: boolean } | null;
  if (!parsed?.id || !parsed.name || !parsed.invite_code) throw new Error('unknown');
  return { id: parsed.id, name: parsed.name, invite_code: parsed.invite_code, is_duel: Boolean(parsed.is_duel) };
}

export async function joinFriendLeague(code: string): Promise<{ id: string; name: string }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('join_friend_league' as never, { p_code: code } as never);
  if (error) throw new Error((error as { message: string }).message);
  const parsed = data as { id?: string; name?: string } | null;
  if (!parsed?.id || !parsed.name) throw new Error('unknown');
  return { id: parsed.id, name: parsed.name };
}

export async function leaveFriendLeague(leagueId: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('leave_friend_league' as never, { p_league_id: leagueId } as never);
  if (error) throw new Error((error as { message: string }).message);
}

// --- Prize Draw: spend earned coins on tickets, win the weekly pot -----------

export interface PrizeDraw {
  id: string;
  title: string;
  pot_coins: number;
  ticket_cost: number;
  draws_at: string;
  my_tickets: number;
  total_tickets: number;
  total_entrants: number;
  my_coins: number;
}
export interface PrizeDrawState {
  draw: PrizeDraw | null;
  last_winner: {
    pot_coins: number;
    drawn_at: string;
    winner_user_id: string;
    winner_name: string;
    avatar_url: string | null;
  } | null;
}

export async function getActivePrizeDraw(): Promise<PrizeDrawState> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_active_prize_draw' as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data as PrizeDrawState | null) ?? { draw: null, last_winner: null };
}

export async function buyPrizeTicket(
  drawId: string,
): Promise<{ my_tickets: number; pot_coins: number; my_coins: number }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('buy_prize_ticket' as never, { p_draw_id: drawId } as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as { my_tickets: number; pot_coins: number; my_coins: number };
}

// --- Exact-Score Prediction game --------------------------------------------

export interface ScorePrediction {
  fixture_id: string;
  home_goals: number;
  away_goals: number;
  points: number | null;
  /** Set once the coin reward has been doubled via a rewarded ad. */
  doubled_at: string | null;
}

export async function listMyScorePredictions(): Promise<ScorePrediction[]> {
  const db = requireSupabase();
  const { data, error } = await db
    .from('score_predictions' as never)
    .select('fixture_id, home_goals, away_goals, points, doubled_at');
  if (error) throw new Error((error as { message: string }).message);
  return ((data ?? []) as unknown) as ScorePrediction[];
}

export async function submitScorePrediction(
  fixtureId: string,
  home: number,
  away: number,
): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('submit_score_prediction' as never, {
    p_fixture_id: fixtureId,
    p_home: home,
    p_away: away,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
}

/** Watch a rewarded ad to double a settled prediction's coin reward (once). */
export async function doubleScoreReward(fixtureId: string): Promise<{ reward: number; coins: number }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('double_score_reward' as never, { p_fixture_id: fixtureId } as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as { reward: number; coins: number };
}

export interface ScorePredictBoardEntry {
  rank: number;
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  points: number;
  exacts: number;
}

export async function getScorePredictBoard(limit = 50): Promise<ScorePredictBoardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_score_predict_board' as never, { p_limit: limit } as never);
  if (error) throw new Error((error as { message: string }).message);
  return ((data ?? []) as unknown) as ScorePredictBoardEntry[];
}

/** Grant coins for a watched rewarded ad (daily-capped server-side). */
export async function grantAdReward(): Promise<{ reward: number; coins: number; remaining: number }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('grant_ad_reward' as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as { reward: number; coins: number; remaining: number };
}

// ---------------------------------------------------------------------------
// Match outcome pick: a virtual-coins 1X2 game (no real money). Stake coins on
// home/draw/away; if right, the stake pays out at the synced multiplier.
// ---------------------------------------------------------------------------

export type MatchOutcomeSide = 'home' | 'draw' | 'away';
export type MatchOutcomeStatus = 'pending' | 'won' | 'lost' | 'void';

/** Coins won PER staked coin for each side (e.g. 2.28 → 100 staked wins 228). */
export interface MatchOutcomeMultipliers {
  home: number;
  draw: number;
  away: number;
}

/** The caller's existing pick for a fixture (locks the card once placed). */
export interface MatchOutcomePickRow {
  pick: MatchOutcomeSide;
  stake: number;
  multiplier: number;
  status: MatchOutcomeStatus;
  payout: number;
}

/**
 * The 1X2 coins game state for a fixture: the per-side multipliers (null until
 * the match has a synced prediction) and the caller's own pick (null when they
 * haven't picked yet). The card silent-hides whenever `multipliers` is null.
 */
export interface MatchOutcomePick {
  multipliers: MatchOutcomeMultipliers | null;
  my_pick: MatchOutcomePickRow | null;
}

/** The 1X2 coins-game state for a fixture (multipliers + the caller's pick). */
export async function getMatchOutcomePick(fixtureId: string): Promise<MatchOutcomePick> {
  const db = requireSupabase();
  // Cast the RPC name/args/result: the generated types lag new functions until
  // `supabase gen types` reruns; the call is valid at runtime.
  const { data, error } = await db.rpc('get_match_outcome_pick' as never, {
    p_fixture_id: fixtureId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  const payload = (data ?? null) as {
    multipliers?: MatchOutcomeMultipliers | null;
    my_pick?: MatchOutcomePickRow | null;
  } | null;
  return {
    multipliers: payload?.multipliers ?? null,
    my_pick: payload?.my_pick ?? null,
  };
}

/**
 * Stake coins on a match outcome. Throws an Error whose message is a bare code
 * (AUTH_REQUIRED, INVALID_PICK, INVALID_STAKE, FIXTURE_NOT_FOUND,
 * PREDICTION_LOCKED, PREDICTION_NOT_AVAILABLE, ALREADY_PICKED,
 * INSUFFICIENT_FUNDS, RATE_LIMITED) - the UI maps these via errorKey().
 */
export async function placeMatchOutcomePick(
  fixtureId: string,
  pick: MatchOutcomeSide,
  stake: number,
): Promise<MatchOutcomePickRow> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('place_match_outcome_pick' as never, {
    p_fixture_id: fixtureId,
    p_pick: pick,
    p_stake: stake,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as MatchOutcomePickRow;
}

// ---------------------------------------------------------------------------
// Predict & Win — WEEKLY CHALLENGES + SEASONAL COINS LEADERBOARD.
// Rotating weekly prediction goals that pay bonus coins, plus a season-wide
// ranking of every user by their coin balance. Backed by migration
// `predict_weekly_challenges` (get_weekly_challenges / get_coins_leaderboard).
// ---------------------------------------------------------------------------

/** What kind of settled-pick event a weekly challenge tracks. */
export type PredictWeeklyEvent = 'pick_settled' | 'pick_won' | 'big_win';

/** One weekly challenge with the caller's live progress against its target. */
export interface PredictWeeklyChallenge {
  key: string;
  event: PredictWeeklyEvent;
  target: number;
  reward_coins: number;
  progress: number;
  completed: boolean;
  claimed_coins: number;
}

/** This week's challenge set + the caller's rolled-up progress/earnings. */
export interface PredictWeeklyChallenges {
  week_start: string;
  challenges: PredictWeeklyChallenge[];
  completed_count: number;
  total_count: number;
  coins_earned: number;
}

/** The caller's weekly Predict & Win challenges (this ISO week, with progress). */
export async function getWeeklyChallenges(): Promise<PredictWeeklyChallenges> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_weekly_challenges' as never);
  if (error) throw new Error((error as { message: string }).message);
  const payload = (data ?? null) as Partial<PredictWeeklyChallenges> | null;
  return {
    week_start: payload?.week_start ?? '',
    challenges: payload?.challenges ?? [],
    completed_count: payload?.completed_count ?? 0,
    total_count: payload?.total_count ?? 0,
    coins_earned: payload?.coins_earned ?? 0,
  };
}

/** One row of the seasonal coins leaderboard. `is_me` flags the caller. */
export interface CoinsLeaderboardEntry {
  rank: number;
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  coins: number;
  is_me: boolean;
}

/**
 * The seasonal coins leaderboard: the top `limit` users by current coin balance,
 * with the caller's own row appended when they fall outside the top N.
 */
export async function getCoinsLeaderboard(limit = 50): Promise<CoinsLeaderboardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_coins_leaderboard' as never, {
    p_limit: limit,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data ?? []) as unknown as CoinsLeaderboardEntry[];
}

/**
 * Best-effort: keep the server's profiles.locale in sync with the app's chosen
 * language, so push notifications (which read profiles.locale) arrive in the same
 * language as the UI. No-op when signed out / web / unconfigured; the next launch
 * or language change retries.
 */
export async function updateMyLocale(locale: string): Promise<void> {
  try {
    const db = requireSupabase();
    const { data } = await db.auth.getUser();
    const userId = data.user?.id;
    if (!userId) return;
    await db
      .from('profiles')
      .update({ locale } as never)
      .eq('id', userId);
  } catch {
    // Non-fatal.
  }
}

/** Today's shared rewarded-ad budget (used/remaining/cap) across all placements. */
export async function getAdRewardState(): Promise<{ used: number; remaining: number; cap: number }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_ad_reward_state' as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data ?? { used: 0, remaining: 10, cap: 10 }) as { used: number; remaining: number; cap: number };
}

// ---------------------------------------------------------------------------
// Live "who scores next?" in-match predictions
// ---------------------------------------------------------------------------

export interface LivePredictionStats {
  /** Open picks on each side (resets after every goal). */
  home: number;
  away: number;
  /** My current open pick, if any. */
  mine: 'home' | 'away' | null;
}

export async function getLivePredictionStats(fixtureId: string): Promise<LivePredictionStats> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_live_prediction_stats' as never, {
    p_fixture_id: fixtureId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data as LivePredictionStats | null) ?? { home: 0, away: 0, mine: null };
}

export async function submitLivePrediction(fixtureId: string, pick: 'home' | 'away'): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('submit_live_prediction' as never, {
    p_fixture_id: fixtureId,
    p_pick: pick,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
}

// ---------------------------------------------------------------------------
// "Pick each group's winner" prediction (the qualification screen)
// ---------------------------------------------------------------------------

export interface GroupWinnerPicks {
  /** team_ids the user picked to top their group. */
  team_ids: string[];
  /** Awarded points once the group stage is settled, else null. */
  points: number | null;
}

export async function getMyGroupWinnerPicks(competitionId: string): Promise<GroupWinnerPicks | null> {
  const db = requireSupabase();
  const { data, error } = await db
    .from('group_winner_predictions' as never)
    .select('team_ids, points')
    .eq('competition_id', competitionId)
    .maybeSingle();
  if (error) throw new Error((error as { message: string }).message);
  return (data as GroupWinnerPicks | null) ?? null;
}

export async function submitGroupWinnerPrediction(competitionId: string, teamIds: string[]): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('submit_group_winner_prediction' as never, {
    p_competition_id: competitionId,
    p_team_ids: teamIds,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
}

// ---------------------------------------------------------------------------
// Player level / XP (derived read-only from every game the user plays)
// ---------------------------------------------------------------------------

export interface PlayerLevel {
  xp: number;
  level: number;
  xp_into_level: number;
  xp_for_next: number;
}

export async function getMyLevel(): Promise<PlayerLevel> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_my_level' as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data as PlayerLevel | null) ?? { xp: 0, level: 1, xp_into_level: 0, xp_for_next: 100 };
}

// ---------------------------------------------------------------------------
// Referral (invite a friend, both get coins)
// ---------------------------------------------------------------------------

export interface MyReferral {
  code: string;
  invited_count: number;
  redeemed: boolean;
}

export async function getMyReferral(): Promise<MyReferral> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_my_referral' as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as MyReferral;
}

export async function redeemReferral(code: string): Promise<{ reward: number; coins: number }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('redeem_referral' as never, { p_code: code } as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as { reward: number; coins: number };
}

/** Watch a rewarded ad to clear the spin cooldown for one extra spin. */
export async function grantAdExtraSpin(fantasySeasonId: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('grant_ad_extra_spin' as never, {
    p_fantasy_season_id: fantasySeasonId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
}

/** Spend 120 coins to clear the spin cooldown for one extra spin. */
export async function buyExtraSpin(seasonId: string): Promise<unknown> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('buy_extra_spin' as never, {
    p_fantasy_season_id: seasonId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return data;
}

// ---------------------------------------------------------------------------
// Predictions game (points, not betting)
// ---------------------------------------------------------------------------

export async function listMyPredictions(): Promise<Tables<'match_predictions'>[]> {
  const db = requireSupabase();
  return unwrap(await db.from('match_predictions').select('*'));
}

export async function savePrediction(fixtureId: string, pick: PredictionPick): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('save_prediction', { p_fixture_id: fixtureId, p_pick: pick });
  if (error) throw new Error(error.message);
}

export interface PredictionTally {
  home: number;
  draw: number;
  away: number;
  total: number;
}

/** Community vote counts for a fixture's 1X2 prediction (counts only). */
export async function getPredictionTally(fixtureId: string): Promise<PredictionTally> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_prediction_tally', { p_fixture_id: fixtureId });
  if (error) throw new Error(error.message);
  const row = data?.[0];
  const home = row?.home_count ?? 0;
  const draw = row?.draw_count ?? 0;
  const away = row?.away_count ?? 0;
  return { home, draw, away, total: home + draw + away };
}

export async function getPredictionLeaderboard(
  limit = 50,
  offset = 0,
): Promise<PredictionBoardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_prediction_leaderboard', {
    p_limit: limit,
    p_offset: offset,
  });
  if (error) throw new Error(error.message);
  return data ?? [];
}

/** Save the caller's whole World Cup bracket prediction. */
export async function saveBracketPrediction(
  seasonId: string | null,
  payload: BracketPayload,
): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('save_bracket_prediction', {
    p_season_id: seasonId,
    p_payload: payload as unknown as Json,
  });
  if (error) throw new Error(error.message);
}

/** The caller's saved bracket payload, or null if they haven't predicted yet. */
export async function getMyBracket(): Promise<BracketPayload | null> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_my_bracket');
  if (error) throw new Error(error.message);
  return (data as BracketPayload | null) ?? null;
}

/** Ranked bracket-predictor leaderboard (by group-prediction accuracy so far). */
export async function getBracketLeaderboard(limit = 50, offset = 0): Promise<BracketBoardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_bracket_leaderboard', { p_limit: limit, p_offset: offset });
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function getMyBracketStats(): Promise<{ points: number; rank: number } | null> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_my_bracket_stats');
  if (error) throw new Error(error.message);
  return data && data.length > 0 ? data[0] : null;
}

/** A bracket-contest prize the caller won (the WC final has been settled). */
export interface BracketPrize {
  rank: number;
  coins: number;
  awarded_at: string;
}

/** The caller's World Cup bracket prize, or null if they didn't place / no settle yet. */
export async function getMyBracketPrize(): Promise<BracketPrize | null> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_my_bracket_prize' as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data as unknown as BracketPrize | null) ?? null;
}

// ---------------------------------------------------------------------------
// Spin Boost (the 24h bonus mini-game).
// ---------------------------------------------------------------------------
export interface SpinStatePick {
  player_id: string;
  spun_at: string;
}

export interface SpinState {
  has_team: boolean;
  gameweek_live: boolean;
  next_spin_at: string;
  can_spin: boolean;
  picks: SpinStatePick[];
}

/** The caller's spin status for a season: cooldown, live-lock and active picks. */
export async function getSpinState(fantasySeasonId: string): Promise<SpinState> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_spin_state', {
    p_fantasy_season_id: fantasySeasonId,
  });
  if (error) throw new Error(error.message);
  return (data ?? {
    has_team: false,
    gameweek_live: false,
    next_spin_at: new Date().toISOString(),
    can_spin: false,
    picks: [],
  }) as unknown as SpinState;
}

/** Lock in 1-2 same-nation bonus players. Returns the refreshed spin state. */
export async function takeSpin(fantasySeasonId: string, playerIds: string[]): Promise<SpinState> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('take_spin', {
    p_fantasy_season_id: fantasySeasonId,
    p_player_ids: playerIds,
  });
  if (error) throw new Error(error.message);
  return data as unknown as SpinState;
}

/** The mercato feed: recent real-world player moves, newest first. */
export async function getTransferFeed(limit = 40, offset = 0): Promise<TransferFeedEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_transfer_feed', { p_limit: limit, p_offset: offset });
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function getPredictionLeagueBoard(
  leagueId: string,
): Promise<PredictionBoardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_prediction_league_board', { p_league_id: leagueId });
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function getMyPredictionStats(): Promise<{
  total_points: number;
  correct: number;
  played: number;
  rank: number;
} | null> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_my_prediction_stats');
  if (error) throw new Error(error.message);
  return data && data.length > 0 ? data[0] : null;
}

/**
 * The user's single most-recent SETTLED WINNING prediction across all three
 * games, or null when there's nothing to celebrate. Used to auto-pop the brag
 * card on launch (see WinCelebrationGate).
 *
 * Each game stores "won" + its settle timestamp differently:
 *   - match_predictions  (1X2)        : points > 0,           scored_at
 *   - score_predictions  (exact score): points > 0,           scored_at
 *   - match_outcome_picks(coins 1X2)  : status = 'won',       settled_at
 *
 * We pull the newest winning row from each (cheap — one indexed row each, with a
 * lean fixture+teams join for the headline), then keep the single newest across
 * the three by its settle timestamp. RLS already scopes every table to the
 * caller, so no explicit user filter is needed. Returns the fixture teams (raw
 * English names — the hook localises) and score plus the reward, so the gate can
 * name the win without a second round-trip.
 */
export interface RecentWin {
  /** Stable id for the AsyncStorage "already celebrated" marker. */
  game: 'outcome' | 'score' | 'exact';
  /** ISO settle timestamp — newer than the stored marker => show the pop. */
  settledAt: string;
  homeTeam: { name: string | null; short_name: string | null } | null;
  awayTeam: { name: string | null; short_name: string | null } | null;
  /** Final score when known (cheap on the fixture row); null pre-score. */
  homeScore: number | null;
  awayScore: number | null;
  /** Prediction points won (1X2 / exact games); 0 for the coins game. */
  points: number;
  /** Coins won (the coins game); 0 for the points games. */
  coins: number;
}

type WinFixtureJoin = {
  home_score: number | null;
  away_score: number | null;
  home_team: { name: string | null; short_name: string | null } | null;
  away_team: { name: string | null; short_name: string | null } | null;
};

const WIN_FIXTURE_SELECT = `home_score, away_score,
  home_team:teams!fixtures_home_team_id_fkey(name, short_name),
  away_team:teams!fixtures_away_team_id_fkey(name, short_name)`;

export async function getRecentWin(): Promise<RecentWin | null> {
  const db = requireSupabase();

  // Each table is queried for its newest winning row only (limit 1). The three
  // run in parallel; any single failure degrades to "no win" rather than
  // throwing, so a transient error never blocks the launch celebration.
  const [outcomeRows, mpRows, spRows] = await Promise.all([
    db
      .from('match_outcome_picks')
      .select(`payout, settled_at, fixture:fixtures!match_outcome_picks_fixture_id_fkey(${WIN_FIXTURE_SELECT})`)
      .eq('status', 'won')
      .not('settled_at', 'is', null)
      .order('settled_at', { ascending: false })
      .limit(1),
    db
      .from('match_predictions')
      .select(`points, scored_at, fixture:fixtures!match_predictions_fixture_id_fkey(${WIN_FIXTURE_SELECT})`)
      .gt('points', 0)
      .not('scored_at', 'is', null)
      .order('scored_at', { ascending: false })
      .limit(1),
    db
      .from('score_predictions')
      .select(`points, scored_at, fixture:fixtures!score_predictions_fixture_id_fkey(${WIN_FIXTURE_SELECT})`)
      .gt('points', 0)
      .not('scored_at', 'is', null)
      .order('scored_at', { ascending: false })
      .limit(1),
  ]);

  const candidates: RecentWin[] = [];

  const pushFrom = (
    rows: { data: unknown[] | null; error: { message: string } | null },
    map: (row: Record<string, unknown>) => RecentWin | null,
  ) => {
    // Swallow per-game errors (a missing table in an older DB, RLS, transient
    // network) — a failed game just can't contribute a win this launch.
    if (rows.error || !rows.data || rows.data.length === 0) return;
    const win = map(rows.data[0] as Record<string, unknown>);
    if (win) candidates.push(win);
  };

  const fixtureOf = (row: Record<string, unknown>): WinFixtureJoin | null => {
    const f = row.fixture as WinFixtureJoin | WinFixtureJoin[] | null | undefined;
    // PostgREST returns the embedded row as an object for a to-one relation, but
    // some client typings widen it to an array — normalise either shape.
    return Array.isArray(f) ? (f[0] ?? null) : (f ?? null);
  };

  pushFrom(outcomeRows as never, (row) => {
    const settledAt = row.settled_at as string | null;
    if (!settledAt) return null;
    const f = fixtureOf(row);
    return {
      game: 'outcome',
      settledAt,
      homeTeam: f?.home_team ?? null,
      awayTeam: f?.away_team ?? null,
      homeScore: f?.home_score ?? null,
      awayScore: f?.away_score ?? null,
      points: 0,
      coins: Number(row.payout ?? 0),
    };
  });

  pushFrom(mpRows as never, (row) => {
    const settledAt = row.scored_at as string | null;
    if (!settledAt) return null;
    const f = fixtureOf(row);
    return {
      game: 'score',
      settledAt,
      homeTeam: f?.home_team ?? null,
      awayTeam: f?.away_team ?? null,
      homeScore: f?.home_score ?? null,
      awayScore: f?.away_score ?? null,
      points: Number(row.points ?? 0),
      coins: 0,
    };
  });

  pushFrom(spRows as never, (row) => {
    const settledAt = row.scored_at as string | null;
    if (!settledAt) return null;
    const f = fixtureOf(row);
    return {
      game: 'exact',
      settledAt,
      homeTeam: f?.home_team ?? null,
      awayTeam: f?.away_team ?? null,
      homeScore: f?.home_score ?? null,
      awayScore: f?.away_score ?? null,
      points: Number(row.points ?? 0),
      coins: 0,
    };
  });

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.settledAt.localeCompare(a.settledAt));
  return candidates[0];
}

export async function listMyPredictionLeagues(): Promise<Tables<'prediction_leagues'>[]> {
  const db = requireSupabase();
  return unwrap(await db.from('prediction_leagues').select('*').order('created_at'));
}

export async function createPredictionLeague(
  name: string,
): Promise<{ id: string; name: string; invite_code: string }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('create_prediction_league', { p_name: name });
  if (error) throw new Error(error.message);
  const parsed = data as { id?: string; name?: string; invite_code?: string } | null;
  if (!parsed?.id || !parsed.name || !parsed.invite_code) throw new Error('unknown');
  return { id: parsed.id, name: parsed.name, invite_code: parsed.invite_code };
}

export async function joinPredictionLeague(code: string): Promise<{ id: string; name: string }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('join_prediction_league', { p_code: code });
  if (error) throw new Error(error.message);
  const parsed = data as { id?: string; name?: string } | null;
  if (!parsed?.id || !parsed.name) throw new Error('unknown');
  return { id: parsed.id, name: parsed.name };
}

export async function leavePredictionLeague(leagueId: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('leave_prediction_league', { p_league_id: leagueId });
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Pick'em Acca of the Day (daily all-or-nothing parlay).
// ---------------------------------------------------------------------------
export type AccaSelection = 'home' | 'draw' | 'away';

export interface AccaFixture {
  fixture_id: string;
  kickoff_at: string;
  status: string;
  locked: boolean;
  home: { name: string; logo: string | null };
  away: { name: string; logo: string | null };
  result: AccaSelection | null;
  my_selection: AccaSelection | null;
}

export interface AccaState {
  acca_date: string | null;
  fixtures: AccaFixture[];
  picks_count: number;
  potential_points: number;
  settled: { legs: number; hits: number; all_correct: boolean; points: number } | null;
}

export async function getAccaState(): Promise<AccaState> {
  const db = requireSupabase();
  // Cast the RPC name/args/result: the generated types lag new functions until
  // `supabase gen types` reruns; the call is valid at runtime.
  const { data, error } = await db.rpc('get_acca_state' as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as unknown as AccaState;
}

export async function submitAccaPick(fixtureId: string, selection: AccaSelection): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('submit_acca_pick' as never, {
    p_fixture_id: fixtureId,
    p_selection: selection,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
}

export async function removeAccaPick(fixtureId: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('remove_acca_pick' as never, { p_fixture_id: fixtureId } as never);
  if (error) throw new Error((error as { message: string }).message);
}

export interface AccaBoardEntry {
  rank: number;
  user_id: string;
  display_name: string;
  total_points: number;
  days_won: number;
  days_played: number;
  avatar_url: string | null;
}

export async function getAccaLeaderboard(limit = 50, offset = 0): Promise<AccaBoardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_acca_leaderboard' as never, {
    p_limit: limit,
    p_offset: offset,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data ?? []) as unknown as AccaBoardEntry[];
}

// ---------------------------------------------------------------------------
// Team of the Round (best XI of a finished gameweek).
// ---------------------------------------------------------------------------
export interface TotrPlayer {
  player_id: string;
  name: string;
  known_as: string | null;
  photo_url: string | null;
  position: 'GK' | 'DEF' | 'MID' | 'FWD';
  points: number;
  team_name: string | null;
  team_logo: string | null;
}

export interface TeamOfRound {
  gameweek_id: string | null;
  round_label: string | null;
  players: TotrPlayer[];
  total: number;
  captain: { player_id: string; name: string; known_as: string | null; points: number } | null;
}

export async function getTeamOfRound(): Promise<TeamOfRound> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_team_of_round' as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as unknown as TeamOfRound;
}

// ---------------------------------------------------------------------------
// Survivor / Last-Man-Standing.
// ---------------------------------------------------------------------------
export interface SurvivorOption {
  team_id: string;
  name: string;
  logo: string | null;
  fixture_id: string;
  opponent_name: string | null;
  kickoff_at: string;
}

export interface SurvivorMyPick {
  team_id: string;
  team_name: string;
  team_logo: string | null;
  opponent_name: string | null;
  locked: boolean;
  result: 'win' | 'elim' | null;
}

export interface SurvivorHistoryEntry {
  round_label: string | null;
  team_name: string;
  team_logo: string | null;
  result: 'win' | 'elim' | null;
}

export interface SurvivorCurrent {
  gameweek_id: string;
  round_label: string | null;
  deadline_at: string;
  options: SurvivorOption[];
  my_pick: SurvivorMyPick | null;
}

export interface SurvivorState {
  status: 'not_joined' | 'alive' | 'out';
  rounds_survived: number;
  eliminated_round: string | null;
  used_team_ids: string[];
  current: SurvivorCurrent | null;
  history: SurvivorHistoryEntry[];
}

export async function getSurvivorState(): Promise<SurvivorState> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_survivor_state' as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as unknown as SurvivorState;
}

export async function submitSurvivorPick(teamId: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('submit_survivor_pick' as never, { p_team_id: teamId } as never);
  if (error) throw new Error((error as { message: string }).message);
}

export interface SurvivorBoardEntry {
  rank: number;
  user_id: string;
  display_name: string;
  rounds_survived: number;
  alive: boolean;
  avatar_url: string | null;
}

export async function getSurvivorLeaderboard(limit = 50, offset = 0): Promise<SurvivorBoardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_survivor_leaderboard' as never, {
    p_limit: limit,
    p_offset: offset,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data ?? []) as unknown as SurvivorBoardEntry[];
}

// ---------------------------------------------------------------------------
// Rewards: daily streak + Volt Coins wallet + achievements.
// ---------------------------------------------------------------------------
export interface Wallet {
  coins: number;
  streak: number;
  longest_streak: number;
  checked_in_today: boolean;
  streak_freezes: number;
}

export interface CheckinResult extends Wallet {
  reward: number;
  already_checked_in: boolean;
}

export interface AchievementState {
  key: string;
  unlocked_at: string | null;
}

export async function getWallet(): Promise<Wallet> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_wallet' as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as unknown as Wallet;
}

export async function dailyCheckin(): Promise<CheckinResult> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('daily_checkin' as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as unknown as CheckinResult;
}

/** Spend 150 coins for +1 streak freeze (capped at 3) that auto-saves a missed day. */
export async function buyStreakFreeze(): Promise<{ coins: number; streak_freezes: number }> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('buy_streak_freeze' as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as unknown as { coins: number; streak_freezes: number };
}

export async function syncMyAchievements(): Promise<AchievementState[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('sync_my_achievements' as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data ?? []) as unknown as AchievementState[];
}

// ---------------------------------------------------------------------------
// Daily missions + by-day streak calendar (stronger Rewards).
// ---------------------------------------------------------------------------
export interface DailyMission {
  key: string;
  reward_coins: number;
  target: number;
  progress: number;
  completed: boolean;
}

export async function getDailyMissions(): Promise<DailyMission[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_daily_missions' as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data ?? []) as unknown as DailyMission[];
}

export async function getStreakDays(): Promise<string[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_streak_days' as never);
  if (error) throw new Error((error as { message: string }).message);
  return ((data ?? []) as unknown as { day: string }[]).map((r) => r.day);
}

/** Fire-and-forget mission progress; never throws into a mutation's happy path. */
export async function recordMissionEvent(eventType: string, count = 1): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('record_mission_event' as never, {
    p_event_type: eventType,
    p_count: count,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
}

// ---------------------------------------------------------------------------
// Coins Store: spend Volt Coins on cosmetic avatar frames.
// ---------------------------------------------------------------------------
export interface StoreItem {
  key: string;
  label_key: string;
  cost_coins: number;
  kind: string;
  owned: boolean;
  equipped: boolean;
}

export interface StoreState {
  balance: number;
  items: StoreItem[];
}

export async function getStore(): Promise<StoreState> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_store' as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as unknown as StoreState;
}

export async function purchaseStoreItem(itemKey: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('purchase_store_item' as never, { p_key: itemKey } as never);
  if (error) throw new Error((error as { message: string }).message);
}

export async function equipAvatarFrame(itemKey: string | null): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('equip_avatar_frame' as never, { p_key: itemKey } as never);
  if (error) throw new Error((error as { message: string }).message);
}

export async function getEquippedFrame(): Promise<string | null> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_equipped_frame' as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data as string | null) ?? null;
}

// ---------------------------------------------------------------------------
// Weekly prize leaderboards: the top managers of each round win coins.
// ---------------------------------------------------------------------------
export interface RecentPrize {
  rank: number;
  coins: number;
  round_label: string | null;
  awarded_at: string;
}

export async function getMyRecentPrize(): Promise<RecentPrize | null> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_my_recent_prize' as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data as unknown as RecentPrize | null) ?? null;
}

// ---------------------------------------------------------------------------
// "If the groups ended now" qualification projection (computed from standings).
// ---------------------------------------------------------------------------
export type QualStatus = 'qualified' | 'third_in' | 'third_out' | 'out';

export interface QualGroupTeam {
  team_id: string;
  name: string;
  logo: string | null;
  rank: number;
  points: number;
  gd: number;
  status: QualStatus;
}

export interface QualGroup {
  group_label: string;
  teams: QualGroupTeam[];
}

export async function getQualificationProjection(
  competitionId: string,
  season: number,
): Promise<QualGroup[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_qualification_projection' as never, {
    p_competition_id: competitionId,
    p_season: season,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data ?? []) as unknown as QualGroup[];
}

// ---------------------------------------------------------------------------
// Golden Boot predictor: pick the tournament top scorer; watch the live race.
// ---------------------------------------------------------------------------
export interface GoldenBootScorer {
  player_id: string;
  name: string;
  known_as: string | null;
  photo_url: string | null;
  team_logo: string | null;
  /** Team uuid — used to dim/badge a scorer whose nation is knocked out. */
  team_id: string | null;
  goals: number;
  assists: number;
  rank: number;
}

export interface GoldenBootPick {
  player_id: string;
  name: string;
  known_as: string | null;
  photo_url: string | null;
  team_logo: string | null;
  team_id: string | null;
  goals: number;
  rank: number | null;
}

export interface GoldenBootState {
  leaders: GoldenBootScorer[];
  my_pick: GoldenBootPick | null;
  total_picks: number;
}

export async function getGoldenBoot(seasonId: string): Promise<GoldenBootState> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_golden_boot' as never, { p_season_id: seasonId } as never);
  if (error) throw new Error((error as { message: string }).message);
  return data as unknown as GoldenBootState;
}

export async function submitGoldenBootPick(seasonId: string, playerId: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('submit_golden_boot_pick' as never, {
    p_season_id: seasonId,
    p_player_id: playerId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
}

// ---------------------------------------------------------------------------
// Team of the Round - PREDICTION game (predict the XI, score vs the real XI).
// ---------------------------------------------------------------------------
export interface TotrPredictionPlayer {
  player_id: string;
  is_captain: boolean;
  name: string;
  known_as: string | null;
  photo_url: string | null;
  position: 'GK' | 'DEF' | 'MID' | 'FWD';
  team_logo: string | null;
}

export interface TotrRound {
  // The PREDICT round (next upcoming gameweek).
  gameweek_id: string | null;
  round_label: string | null;
  deadline_at: string | null;
  locked: boolean;
  status: string | null;
  my_prediction: TotrPredictionPlayer[];
  // The SHOWCASE round (latest gameweek with scores, live or finished): its real
  // best XI is the headline, with the caller's own prediction + result for it.
  showcase_round_label: string | null;
  showcase_status: string | null;
  actual_xi: TeamOfRound | null;
  showcase_prediction: TotrPredictionPlayer[];
  showcase_settled: { hits: number; captain_hit: boolean; points: number } | null;
}

export async function getTotrRound(gameweekId?: string): Promise<TotrRound> {
  const db = requireSupabase();
  const { data, error } = await db.rpc(
    'get_totr_round' as never,
    (gameweekId ? { p_gameweek_id: gameweekId } : {}) as never,
  );
  if (error) throw new Error((error as { message: string }).message);
  return data as unknown as TotrRound;
}

export async function submitTotrPrediction(
  gameweekId: string,
  playerIds: string[],
  captainId: string,
): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('submit_totr_prediction' as never, {
    p_gameweek_id: gameweekId,
    p_player_ids: playerIds,
    p_captain_id: captainId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
}

export interface TotrBoardEntry {
  rank: number;
  user_id: string;
  display_name: string;
  total_points: number;
  rounds_played: number;
  best_round: number;
  avatar_url: string | null;
}

export async function getTotrLeaderboard(limit = 50, offset = 0): Promise<TotrBoardEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_totr_leaderboard' as never, {
    p_limit: limit,
    p_offset: offset,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data ?? []) as unknown as TotrBoardEntry[];
}

// ---------------------------------------------------------------------------
// Esports & Sports (Bzzoiro Sports Addon, via the lazy `sports-events` edge fn).
// A side is a TEAM (csgo/hockey/basketball) or a PLAYER (tennis/darts); the UI
// renders both with one card. score_a/b are maps/sets/points (null pre-match).
// ---------------------------------------------------------------------------

/** The five supported disciplines (default to tennis - it carries the most data). */
export type SportId = 'tennis' | 'csgo' | 'darts' | 'hockey' | 'basketball';

/** Which slice of the schedule to show. */
export type SportsMode = 'live' | 'upcoming' | 'finished';

/**
 * One fixture in a sport's schedule. country_a/b are ISO codes (→ flag emoji).
 * The edge functions emit `string | null` for the text fields (a one-sided /
 * malformed upstream row leaves a side, tournament, round or label null), so the
 * client types them nullable and coalesces at render — a null side must never
 * crash the list (the `monogram(null)` TypeError).
 */
export interface SportsEvent {
  id: string;
  tournament: string | null;
  side_a: string | null;
  side_b: string | null;
  score_a: number | null;
  score_b: number | null;
  country_a: string;
  country_b: string;
  status: string | null;
  date: string | null;
  round: string | null;
  live_label: string | null;
}

/** The `sports-events` edge function payload (one sport + mode). */
export interface SportsEventsResult {
  sport: SportId;
  mode: SportsMode;
  /** true → sides are teams (crest-less), false → sides are individual players. */
  is_team: boolean;
  events: SportsEvent[];
}

/**
 * The schedule for one discipline + slice, via the lazy `sports-events` edge
 * function (Bzzoiro Sports Addon). Returns an empty list on any error so the
 * screen shows a friendly per-sport empty state rather than a broken card -
 * hockey / darts are frequently off-season.
 */
export async function getSportsEvents(
  sport: SportId,
  mode: SportsMode,
): Promise<SportsEventsResult> {
  const db = requireSupabase();
  const { data, error } = await db.functions.invoke<SportsEventsResult>('sports-events', {
    body: { sport, mode },
  });
  if (error || !data) {
    return { sport, mode, is_team: false, events: [] };
  }
  return {
    sport: data.sport ?? sport,
    mode: data.mode ?? mode,
    is_team: Boolean(data.is_team),
    events: Array.isArray(data.events) ? data.events : [],
  };
}

// ---------------------------------------------------------------------------
// Esports & Sports — extra disciplines from 365scores (via the lazy `scores-365`
// edge fn). These are the team sports Bzzoiro lacks. The edge function normalizes
// 365's `/match/list` into the SAME SportsEvent rows as `sports-events`, so the
// screen renders both providers with one card. All five are team-vs-team.
// ---------------------------------------------------------------------------

/** 365scores numeric sportIds we support: basketball, handball, am.football, baseball, volleyball, rugby. */
export type Scores365SportId = 2 | 5 | 6 | 7 | 8 | 9;

/**
 * Which discipline each chip maps to. BZZOIRO ids hit `sports-events`; the 365
 * ids hit `scores-365`. The screen uses this map to pick the right hook so a
 * single chip strip can mix both providers. Basketball routes through 365 (it
 * carries far more games than Bzzoiro).
 */
export const SCORES_365_SPORTS = {
  basketball: 2,
  americanfootball: 6,
  baseball: 7,
  volleyball: 8,
  handball: 5,
  rugby: 9,
} as const satisfies Record<string, Scores365SportId>;

/** A chip id sourced from 365scores (the keys of SCORES_365_SPORTS). */
export type Scores365Sport = keyof typeof SCORES_365_SPORTS;

/** The `scores-365` edge function payload (one 365 sport + mode + optional day). */
export interface Scores365Result {
  sportId: Scores365SportId;
  mode: SportsMode;
  /** The calendar day (YYYY-MM-DD) served, when the caller requested one. */
  date: string | null;
  is_team: boolean;
  events: SportsEvent[];
}

/**
 * The schedule for one 365scores discipline + slice, via the lazy `scores-365`
 * edge function. With `date` (YYYY-MM-DD) the function serves exactly that
 * calendar day — the screen's Yesterday/Today/Tomorrow strip drives it; with no
 * date it keeps the legacy today (+ thin-upcoming tomorrow merge) behaviour.
 * Returns an empty list on any error so the screen shows the same friendly
 * per-sport empty state as Bzzoiro — these sports are frequently off-season
 * (handball / am. football / rugby in summer).
 */
export async function getScores365(
  sportId: Scores365SportId,
  mode: SportsMode,
  date?: string,
): Promise<Scores365Result> {
  const db = requireSupabase();
  // Only forward a date when given — an absent key preserves the function's
  // legacy today/tomorrow path.
  const body = date ? { sportId, mode, date } : { sportId, mode };
  const { data, error } = await db.functions.invoke<Scores365Result>('scores-365', {
    body,
  });
  if (error || !data) {
    return { sportId, mode, date: date ?? null, is_team: true, events: [] };
  }
  return {
    sportId: (data.sportId as Scores365SportId) ?? sportId,
    mode: data.mode ?? mode,
    date: data.date ?? date ?? null,
    is_team: data.is_team !== false,
    events: Array.isArray(data.events) ? data.events : [],
  };
}

// --- Live ball-tracker WebSocket credentials (bzzoiro-ws-token edge fn) -------

/** A short-lived credential pair for the Bzzoiro live football WebSocket. */
export interface BzzoiroWsToken {
  /** The per-session auth token appended to the socket url as `?token=`. */
  token: string;
  /** The base socket url, e.g. `wss://sports.bzzoiro.com/live/football/`. */
  ws_url: string;
}

/**
 * Mint a Bzzoiro live-socket credential via the authenticated `bzzoiro-ws-token`
 * edge function. It 401s for anonymous callers (logged-in users only), so this
 * returns null on ANY failure (auth, network, malformed payload) and the live
 * pitch degrades to its connection-error state rather than throwing. The caller
 * caches this through React Query (~50min staleTime). The token/url are secrets:
 * never log them and never hardcode them.
 */
export async function getBzzoiroWsToken(): Promise<BzzoiroWsToken | null> {
  const db = requireSupabase();
  const { data, error } = await db.functions.invoke<BzzoiroWsToken>('bzzoiro-ws-token');
  if (error || !data || typeof data.token !== 'string' || typeof data.ws_url !== 'string') {
    return null;
  }
  return { token: data.token, ws_url: data.ws_url };
}

// --- 365scores news + competition history (migration 128) --------------------

/**
 * One news article from 365scores (the get_wc_news RPC). `scope` is
 * 'competition' for World-Cup-wide news, 'match' for a specific fixture's news
 * (then `fixture_id` is set). `source_name` is the resolved outlet (e.g. "BBC").
 *
 * Named WcNewsArticle to avoid colliding with the existing RSS `NewsArticle`
 * (Tables<'news_articles'>); this is the 365scores-sourced World Cup feed.
 */
export interface WcNewsArticle {
  id: number;
  scope: 'competition' | 'match';
  fixture_id: string | null;
  source_name: string | null;
  title: string | null;
  image_url: string | null;
  url: string | null;
  published_at: string | null;
  is_magazine: boolean;
}

/**
 * World Cup news, newest first, via the get_wc_news RPC.
 *   * no fixtureId  -> competition-wide news,
 *   * fixtureId set -> that match's news PLUS the general competition news.
 * Returns [] on any error / when nothing is synced (the UI silent-hides on
 * empty). Granted to anon so it shows pre-login.
 */
export async function getWcNews(fixtureId?: string, limit = 30): Promise<WcNewsArticle[]> {
  const db = requireSupabase();
  // Cast the RPC name/args/result: the generated types lag new functions until
  // `supabase gen types` reruns; the call is valid at runtime.
  const { data, error } = await db.rpc('get_wc_news' as never, {
    p_fixture_id: fixtureId ?? null,
    p_limit: limit,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return Array.isArray(data) ? (data as WcNewsArticle[]) : [];
}

/** The 365scores competitionId for the FIFA World Cup (history key). */
export const WC_365_COMPETITION_ID = 5930;

/** One finalist in a past World Cup final (from the history blob). */
export interface WcHistoryParticipant {
  name: string;
  symbolicName: string | null;
  competitorId: number | null;
  /** 365 cue: the qualified/advanced side of the tie (i.e. the winner). */
  isQualified: boolean;
  /** 365 cue: 1 = listed first, often the winner; null when absent. */
  originPosition: number | null;
}

/** One past World Cup final: the season and its two finalists. */
export interface WcHistoryFinal {
  seasonNum: number | null;
  /** The stage label, e.g. "Final". */
  groupName: string | null;
  participants: WcHistoryParticipant[];
}

/**
 * Parse the raw 365 competition-history `data` blob into the finals list the
 * WcHistory card renders. Tolerant of missing/renamed fields: any row that
 * yields no participants is dropped, and a fully empty/garbled blob returns [].
 */
export function parseWcHistory(raw: Json | null): WcHistoryFinal[] {
  const rows = (raw as { table?: { rows?: unknown[] } } | null)?.table?.rows;
  if (!Array.isArray(rows)) return [];
  const finals: WcHistoryFinal[] = [];
  for (const row of rows) {
    const r = row as {
      seasonNum?: unknown;
      group?: { name?: unknown; participants?: unknown[] };
    };
    const rawParticipants = Array.isArray(r.group?.participants) ? r.group?.participants : [];
    const participants: WcHistoryParticipant[] = rawParticipants
      .map((participant) => {
        const p = participant as Record<string, unknown>;
        const name = typeof p.name === 'string' ? p.name : null;
        if (!name) return null;
        return {
          name,
          symbolicName: typeof p.symbolicName === 'string' ? p.symbolicName : null,
          competitorId: typeof p.competitorId === 'number' ? p.competitorId : null,
          isQualified: p.isQualified === true,
          originPosition: typeof p.originPosition === 'number' ? p.originPosition : null,
        } satisfies WcHistoryParticipant;
      })
      .filter((participant): participant is WcHistoryParticipant => participant !== null);
    // A final has exactly two finalists; drop any malformed row so consumers can
    // safely destructure both sides (a 1-participant row would crash the card).
    if (participants.length !== 2) continue;
    finals.push({
      seasonNum: typeof r.seasonNum === 'number' ? r.seasonNum : null,
      groupName: typeof r.group?.name === 'string' ? r.group?.name : null,
      participants,
    });
  }
  // Newest first so the most recent final leads the card.
  return finals.sort((a, b) => (b.seasonNum ?? 0) - (a.seasonNum ?? 0));
}

/**
 * The competition's history blob (past finals / winners) via the
 * get_competition_history RPC. Returns the raw 365 `data` jsonb (the caller
 * renders it) or null when not synced. Defaults to the World Cup id. Granted to
 * anon so it shows pre-login.
 */
export async function getCompetitionHistory(
  providerId: number = WC_365_COMPETITION_ID,
): Promise<Json | null> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_competition_history' as never, {
    p_provider_id: providerId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return (data ?? null) as Json | null;
}

// --- 365scores RICH standings + knockout bracket (migration 129) -------------

/**
 * The 365 competitor crest url, keyed by competitor id + its `imageVersion` (so a
 * kit/badge change busts the CDN cache). Confirmed to return a 200 image/png.
 * sync-365 already bakes this url into the cached standings/bracket blobs, so the
 * app rarely needs to call this directly — it's exported for any caller that has
 * a bare (competitorId, imageVersion) pair (e.g. a 365 competitor embedded in
 * another payload).
 */
export function scores365Crest(competitorId: number, imageVersion: number | null): string {
  const v = imageVersion ?? 1;
  return `https://imagecache.365scores.com/image/upload/f_png,w_68,h_68,c_limit,q_auto:eco,dpr_2/v${v}/Competitors/${competitorId}`;
}

/**
 * One team's row in a 365scores WC group (richer than our API-Football standings,
 * migration 7): carries the recent form, matchday trend, qualification
 * destination + FIFA rank + crest sync-365 baked in.
 */
export interface WcStandingRow {
  teamId: number;
  name: string | null;
  symbolicName: string | null;
  /** Ready-to-render crest url (365 image CDN). */
  crest: string;
  /** Current FIFA world ranking position, or null. */
  fifaRank: number | null;
  played: number;
  won: number;
  lost: number;
  draw: number;
  for: number;
  against: number;
  /** Goal difference (for − against). */
  gd: number;
  points: number;
  position: number | null;
  /**
   * Recent results oldest→newest as 365 cues: 1 = win, 2 = loss, 3 = draw.
   * Render as form pips. [] until any games are played.
   */
  recentForm: number[];
  /** Position movement vs the previous matchday (+up / 0 / −down), or null. */
  trend: number | null;
  /** Index into the group's `destinations` legend (see WcStandingGroup-less blob). */
  destinationNum: number | null;
  /** true when the destination is mathematically secured. */
  destinationGuaranteed: boolean;
}

/** One 365scores WC group: its number/name and the rows sorted by position. */
export interface WcStandingGroup {
  num: number | null;
  name: string | null;
  rows: WcStandingRow[];
}

/** One entry in the qualification legend (e.g. "Round of 32" with its color). */
export interface WcStandingDestination {
  num: number | null;
  name: string | null;
  /** Hex color the legend/badge uses, e.g. "#0CF737". */
  color: string | null;
}

/** The full normalized WC standings blob (groups + the destination legend). */
export interface WcStandings {
  groups: WcStandingGroup[];
  destinations: WcStandingDestination[];
}

/**
 * The RICH 365scores WC group standings via the get_scores365_standings RPC.
 * Returns { groups, destinations } or null when not synced yet. Defaults to the
 * World Cup competition id. Granted to anon so it shows pre-login; the UI
 * silent-hides on null/empty groups.
 */
export async function getScores365Standings(
  competition: number = WC_365_COMPETITION_ID,
): Promise<WcStandings | null> {
  const db = requireSupabase();
  // Cast the RPC name/args/result: generated types lag new functions until
  // `supabase gen types` reruns; the call is valid at runtime.
  const { data, error } = await db.rpc('get_scores365_standings' as never, {
    p_competition: competition,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  if (!data) return null;
  const blob = data as Partial<WcStandings>;
  return {
    groups: Array.isArray(blob.groups) ? blob.groups : [],
    destinations: Array.isArray(blob.destinations) ? blob.destinations : [],
  };
}

/**
 * One game in the 365scores knockout-bracket cache. `stageNum` is the key the
 * future bracket UI keys off: 1 = group stage (all WC games for now), >=2 =
 * knockout. `home`/`away` carry a ready crest + (running/final) score.
 */
export interface BracketGame {
  id: number | null;
  /** 1 = group stage; >=2 = knockout (Round of 32, etc.). */
  stageNum: number | null;
  roundNum: number | null;
  /** Stage/round label, e.g. "Round" / "Round of 32" / "Final". */
  roundName: string | null;
  /** Group label when group-stage, e.g. "Group A"; null in knockout. */
  groupName: string | null;
  /** ISO kickoff (365 local tz). */
  startTime: string | null;
  /** 365 status text, e.g. "Scheduled" / "Ended". */
  status: string | null;
  home: BracketGameSide;
  away: BracketGameSide;
}

/** One side of a bracket game (id + name + crest + score). */
export interface BracketGameSide {
  id: number;
  name: string | null;
  symbolicName: string | null;
  /** Ready-to-render crest url. */
  crest: string;
  /** Score (−1 / null before kickoff). */
  score: number | null;
}

/**
 * The 365scores knockout-bracket games (merged /results + /fixtures) via the
 * get_scores365_bracket RPC, chronological. Returns [] when not synced. Until
 * 365 emits stageNum>=2 games these are all group stage (stageNum=1); a future
 * bracket UI filters to stageNum>=2 and auto-activates when knockout games
 * appear — no code change needed. Defaults to the World Cup competition id.
 */
export async function getScores365Bracket(
  competition: number = WC_365_COMPETITION_ID,
): Promise<BracketGame[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_scores365_bracket' as never, {
    p_competition: competition,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  const games = (data as { games?: unknown } | null)?.games;
  return Array.isArray(games) ? (games as BracketGame[]) : [];
}

// --- 365scores RICH player profiles (migration 130 + player-365 edge fn) ------

/**
 * The 365 athlete photo url, keyed by athleteId + imageVersion (so a portrait
 * change busts the CDN cache). Verified HTTP 200 image/png. The CDN tolerates a
 * wrong/zero version, so a null imageVersion (use 1) still resolves. The
 * player-365 edge fn already bakes this url into the profile's `photo`, so the
 * app rarely calls this directly — it's exported for callers that have a bare
 * (athleteId, imageVersion) pair (e.g. a resolved scores365_players row).
 */
export function scores365AthletePhoto(athleteId: number, imageVersion: number | null): string {
  const v = imageVersion ?? 1;
  return `https://imagecache.365scores.com/image/upload/f_png,w_100,h_100,c_limit,q_auto:eco,dpr_2/v${v}/Athletes/${athleteId}`;
}

/**
 * Normalize a player name the SAME way sync-365 does before storing name_norm
 * (lowercase, fold accents, expand '&', strip non-alphanumerics). Callers pass
 * the RESULT of this to resolveScores365Athlete so the lookup key matches the
 * stored one. Kept in lock-step with sync-365's normName (NAME_ALIASES are sync
 * side only — they fold provider name variants we don't see app-side).
 */
function normScores365Name(raw: string): string {
  return raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * Resolve a player NAME to its 365 athleteId via the resolve_scores365_athlete
 * RPC (best exact name_norm match over scores365_players, the directory sync-365
 * fills from mapped fixtures' lineups). The name is normalized here to match the
 * stored key. Returns null when unknown (player not in a synced lineup yet) — the
 * caller then has no rich profile to open. Granted to anon so it resolves
 * pre-login.
 */
export async function resolveScores365Athlete(name: string): Promise<number | null> {
  const trimmed = name?.trim();
  if (!trimmed) return null;
  const db = requireSupabase();
  const { data, error } = await db.rpc('resolve_scores365_athlete' as never, {
    p_name_norm: normScores365Name(trimmed),
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  const id = data as number | null;
  return typeof id === 'number' && Number.isFinite(id) ? id : null;
}

/** A flat {name,value} stat the profile UI renders (e.g. "Goals" → "12"). */
export interface Scores365StatKV {
  name: string;
  value: string;
}

/** One competition block of a player's headline stats. */
export interface Scores365HighlightBlock {
  competition: string | null;
  stats: Scores365StatKV[];
}

/** One row (a competition line) within a career season's table. */
export interface Scores365CareerRow {
  title: string | null;
  stats: Scores365StatKV[];
}

/** One season of a player's career stats. */
export interface Scores365CareerSeason {
  key: string | null;
  name: string | null;
  shortName: string | null;
  rows: Scores365CareerRow[];
}

/** One side of a player's recent match (id + name + crest + score). */
export interface Scores365MatchSide {
  id: number | null;
  name: string | null;
  score: number | null;
  /** Ready-to-render crest url, or null when the side's id is unknown. */
  crest: string | null;
}

/** One of a player's recent matches with their rating. */
export interface Scores365LastMatch {
  gameId: number | null;
  competition: string | null;
  startTime: string | null;
  status: string | null;
  played: boolean;
  home: Scores365MatchSide;
  away: Scores365MatchSide;
  /** The athlete's match rating (e.g. "7.1"), or null. */
  rating: string | null;
  /** The 365 rating-badge background color (hex), or null. */
  ratingColor: string | null;
}

/** One transfer in a player's history, with the resolved club name + crest. */
export interface Scores365Transfer {
  competitorId: number | null;
  teamName: string | null;
  crest: string | null;
  date: string | null;
  /** The transfer kind label (e.g. "Transfer", "Loan"). */
  type: string | null;
  /** The fee as the provider formats it (e.g. "€28.5M"), or null. */
  price: string | null;
  contractUntil: string | null;
  active: boolean;
}

/** One trophy line in a player's honours. */
export interface Scores365Trophy {
  category: string | null;
  name: string | null;
  displayName: string | null;
  count: number;
}

/**
 * A RICH 365scores player profile, normalized by the player-365 edge function
 * from /player/stats. Every list is best-effort (empty when the provider omits
 * that slice). `photo` is the ready-to-render Athletes CDN url.
 */
export interface Scores365Player {
  id: number;
  name: string | null;
  age: number | null;
  position: string | null;
  nationality: string | null;
  isGoalie: boolean;
  shortBio: string | null;
  clubId: number | null;
  nationalTeamId: number | null;
  photo: string;
  highlightStats: Scores365HighlightBlock[];
  careerStats: Scores365CareerSeason[];
  lastMatches: Scores365LastMatch[];
  transfers: Scores365Transfer[];
  trophies: Scores365Trophy[];
}

/** The `player-365` edge function payload (the athleteId echoed + the profile). */
interface Player365Result {
  athleteId: number;
  profile: Scores365Player | null;
  error?: string;
}

/**
 * Fetch a RICH 365scores player profile by athleteId, via the lazy `player-365`
 * edge function. Resolve the athleteId first with resolveScores365Athlete(name)
 * when you only have a name. Returns null on any error / unknown athlete (the
 * function is best-effort and never 500s) so the caller shows a friendly empty
 * state rather than throwing.
 */
export async function getScores365Player(athleteId: number): Promise<Scores365Player | null> {
  if (!Number.isFinite(athleteId) || athleteId <= 0) return null;
  const db = requireSupabase();
  const { data, error } = await db.functions.invoke<Player365Result>('player-365', {
    body: { athleteId },
  });
  if (error || !data || !data.profile) return null;
  return data.profile;
}

// --- 365scores head-to-head (h2h-365 edge fn) --------------------------------

/** One side of an H2H previous meeting (resolved name + crest + score). */
export interface H2H365Side {
  name: string | null;
  /** Ready-to-render crest url, or null when the side's id is unknown. */
  crest: string | null;
  score: number | null;
}

/** One previous meeting between the two teams, oriented as it was played. */
export interface H2H365Meeting {
  /** ISO kickoff of the past meeting, or null. */
  date: string | null;
  /** Competition display name, or null. */
  competition: string | null;
  home: H2H365Side;
  away: H2H365Side;
  /** Which side won that meeting, or null (e.g. abandoned / unknown score). */
  winner: 'home' | 'draw' | 'away' | null;
}

/** One past match in a team's recent-form list, oriented to THAT team. */
export interface H2H365RecentGame {
  /** ISO kickoff of the past match, or null. */
  date: string | null;
  /** Competition display name, or null. */
  competition: string | null;
  /** The opponent in that match (resolved name + crest). */
  opponent: { name: string | null; crest: string | null };
  /** The team's own goals / the opponent's goals in that match. */
  ourScore: number | null;
  oppScore: number | null;
  /** Result from this team's perspective. */
  result: 'W' | 'D' | 'L' | null;
}

/** A team + its recent results — powers the 365-style per-team form list. */
export interface H2H365TeamForm {
  name: string | null;
  crest: string | null;
  recent: H2H365RecentGame[];
}

/**
 * The 365scores HEAD-TO-HEAD block for a fixture, normalized by the `h2h-365`
 * edge function from /match/h2h. `summary` is the aggregate W/D/L oriented to the
 * CURRENT fixture's home team (null when not derivable). `meetings` is the recent
 * previous-meetings list (newest the provider returns first). `home`/`away` carry
 * each side's recent form (last games with W/D/L), like 365's "وجها لوجه" tab.
 * All empty/null when the match has no 365 H2H data — the caller self-hides.
 */
export interface H2H365 {
  summary: { homeWins: number; draws: number; awayWins: number } | null;
  meetings: H2H365Meeting[];
  home: H2H365TeamForm | null;
  away: H2H365TeamForm | null;
}

/** The `h2h-365` edge function payload (the gameId echoed + the H2H block). */
interface H2H365Result {
  gameId: number;
  summary: H2H365['summary'];
  meetings?: H2H365Meeting[];
  home?: H2H365TeamForm | null;
  away?: H2H365TeamForm | null;
  error?: string;
}

/**
 * Fetch the 365scores head-to-head block for a match by its 365 GAME id (stored
 * on the xG stats as `scores365_game_id`), via the lazy `h2h-365` edge function.
 * Returns an empty block (`{ summary: null, meetings: [] }`) on any error /
 * unknown id (the function is best-effort and never 500s) so the caller shows a
 * friendly empty state / self-hides rather than throwing.
 */
export async function getH2H365(gameId: number): Promise<H2H365> {
  const empty: H2H365 = { summary: null, meetings: [], home: null, away: null };
  if (!Number.isFinite(gameId) || gameId <= 0) return empty;
  const db = requireSupabase();
  const { data, error } = await db.functions.invoke<H2H365Result>('h2h-365', {
    body: { gameId },
  });
  if (error || !data) return empty;
  const normForm = (f: H2H365TeamForm | null | undefined): H2H365TeamForm | null =>
    f && Array.isArray(f.recent) ? f : null;
  return {
    summary: data.summary ?? null,
    meetings: Array.isArray(data.meetings) ? data.meetings : [],
    home: normForm(data.home),
    away: normForm(data.away),
  };
}

// ---------------------------------------------------------------------------
// App config (drives the in-app "Update available / required" gate).
// ---------------------------------------------------------------------------

/** Server-published version targets read by the update gate on launch. */
export interface AppConfig {
  /** Newest store build available -> soft, dismissible "update available" nudge. */
  latestVersion: string;
  /** Floor build -> anything below it is hard-blocked ("update required"). */
  minVersion: string;
  /** Kill-switch: hard-block every client regardless of the version targets. */
  forceUpdate: boolean;
}

/** SAFE defaults: never block anyone if the backend is unreachable / unconfigured. */
const DEFAULT_APP_CONFIG: AppConfig = {
  latestVersion: '0.0.0',
  minVersion: '0.0.0',
  forceUpdate: false,
};

/**
 * Read the app_config singleton via the `get_app_config` RPC. Always resolves to
 * a usable config: on any error / missing backend it returns SAFE defaults
 * (0.0.0 floors + no force) so the gate degrades to "no update needed" rather
 * than locking the user out.
 */
export async function getAppConfig(): Promise<AppConfig> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_app_config' as never);
  if (error || !data) return DEFAULT_APP_CONFIG;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { latest_version?: string; min_version?: string; force_update?: boolean }
    | undefined;
  if (!row) return DEFAULT_APP_CONFIG;
  return {
    latestVersion: typeof row.latest_version === 'string' ? row.latest_version : '0.0.0',
    minVersion: typeof row.min_version === 'string' ? row.min_version : '0.0.0',
    forceUpdate: row.force_update === true,
  };
}

// ---------------------------------------------------------------------------
// Sticker Album (Panini-style WC collection)
// ---------------------------------------------------------------------------

/** One pack kind: the FREE once-a-day pack, or a coin-bought pack. */
export type StickerPackKind = 'daily' | 'coins';

/** One sticker granted by a pack open: which player + whether it was new. */
export interface StickerGrant {
  player_id: string;
  is_new: boolean;
}

/** The result of opening a pack: the 5 granted stickers + the new balance. */
export interface PackResult {
  kind: StickerPackKind;
  granted: StickerGrant[];
  coins: number;
  /** Whether today's FREE pack is still available afterwards. */
  free_available: boolean;
}

/** The caller's owned stickers + the pool size + free-pack availability. */
export interface AlbumState {
  owned: { player_id: string; count: number }[];
  total: number;
  free_available: boolean;
}

/** Open a sticker pack (free daily or coin-bought) and grant 5 random WC players. */
export async function openStickerPack(kind: StickerPackKind): Promise<PackResult> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('open_sticker_pack' as never, { p_kind: kind } as never);
  if (error) throw new Error((error as { message: string }).message);
  const payload = (data ?? {}) as Partial<PackResult>;
  return {
    kind,
    granted: payload.granted ?? [],
    coins: payload.coins ?? 0,
    free_available: payload.free_available ?? false,
  };
}

/** The caller's album state: owned player ids + counts, pool size, free availability. */
export async function getMyAlbum(): Promise<AlbumState> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_my_album' as never);
  if (error) throw new Error((error as { message: string }).message);
  const payload = (data ?? {}) as Partial<AlbumState>;
  return {
    owned: payload.owned ?? [],
    total: payload.total ?? 0,
    free_available: payload.free_available ?? false,
  };
}

/** Every World Cup player (sticker pool), with team for the crest/grouping. The
 *  album joins these against the owned ids from getMyAlbum. */
export async function listWorldCupPlayers(): Promise<PlayerWithTeam[]> {
  const db = requireSupabase();
  // The album covers only the nations that reached the knockouts (the 32 in the
  // Round of 32). get_wc_album_team_ids() is the SAME team set the grant pool
  // (open_sticker_pack / wc_player_ids) uses, so the displayed grid and the
  // grantable stickers can never diverge — that mismatch was the old "—" blank.
  const { data: teamIdRows, error: teamErr } = await db.rpc('get_wc_album_team_ids' as never);
  if (teamErr) throw new Error(teamErr.message);
  const teamIds = ((teamIdRows ?? []) as unknown[])
    .map((row) =>
      typeof row === 'string'
        ? row
        : (row as Record<string, string>)?.get_wc_album_team_ids ?? null,
    )
    .filter((id): id is string => Boolean(id));
  if (teamIds.length === 0) return [];
  return unwrap(
    await db
      .from('players')
      .select(`*, team:teams!players_team_id_fkey(${TEAM_REF})`)
      .in('team_id', teamIds)
      // Mirror wc_player_ids: only players with a real photo + name (no blanks).
      .not('photo_url', 'is', null)
      .not('name', 'is', null)
      .neq('name', '')
      .order('fantasy_price', { ascending: false })
      .order('name')
      .limit(2000)
      .returns<PlayerWithTeam[]>(),
  );
}

/**
 * The WC nations that are OUT of the tournament (group-stage non-qualifiers +
 * knockout losers, penalty ties resolved by the stored winner flag). Every WC
 * surface consults this one set to dim/badge eliminated teams. Empty until the
 * knockouts are drawn. SETOF uuid — normalise the PostgREST scalar/object shape.
 */
export async function getWcEliminatedTeamIds(): Promise<string[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_wc_eliminated_team_ids' as never);
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown[])
    .map((row) =>
      typeof row === 'string'
        ? row
        : (row as Record<string, string>)?.get_wc_eliminated_team_ids ?? null,
    )
    .filter((id): id is string => Boolean(id));
}

// ---------------------------------------------------------------------------
// Player of the Match voting
// ---------------------------------------------------------------------------

/** One row of the live PotM tally: a player, their votes, and my-pick flag. */
export interface PotmTallyEntry {
  player_id: string;
  vote_count: number;
  my_vote: boolean;
}

/** Cast (or change) the caller's Player-of-the-Match vote for a fixture. */
export async function votePotm(fixtureId: string, playerId: string): Promise<void> {
  const db = requireSupabase();
  const { error } = await db.rpc('vote_potm' as never, {
    p_fixture_id: fixtureId,
    p_player_id: playerId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
}

/** The live PotM tally for a fixture (desc), flagging the caller's current pick. */
export async function getPotmTally(fixtureId: string): Promise<PotmTallyEntry[]> {
  const db = requireSupabase();
  const { data, error } = await db.rpc('get_potm_tally' as never, {
    p_fixture_id: fixtureId,
  } as never);
  if (error) throw new Error((error as { message: string }).message);
  return ((data ?? []) as PotmTallyEntry[]).map((row) => ({
    player_id: row.player_id,
    vote_count: Number(row.vote_count),
    my_vote: row.my_vote === true,
  }));
}
